import { useEffect, useState } from "react";
// @ts-ignore
import { CustomPlugin, DataSource, DynamicMappingConfig, Environment, FieldMapping, Job, JobStep, JobTaskVersion2, JobVersion2, TargetEntities } from "./interfaces";
import { getEnvironments } from '../../services/user.service';
import { useAuth } from "../../Contexts/AuthContext";
// import mockData from "./mockDataV2.json"

type InitialViewItem = {
    source_entity_id: number;
    target_entity_id: number;
    target_entity_table_constructor: string;
    target_entity_name: string;
    target_entity_status?: string | null;
    source_target_entity_mapping_id: number;
    source_target_entity_mapping_status: string;
    target_entity_dm_id?: number;
    target_entity_dm_status?: string;
    target_entity_rdv_id?: number;
    target_entity_rdv_status?: string;
    target_entity_bdv_id?: number;
    target_entity_bdv_status?: string;
    target_entity_mapping_id?: number;
    target_entity_mapping_status?: string;

    [k: string]: any;
};

export type ViewTargetMapping = {
    target_entity_mapping_id: number | null;
    target_entity_mapping_status: string | null;
};

export type ViewSourceTargetMapping = {
    source_target_entity_mapping_id: number | null;
    source_target_entity_mapping_status: string | null;
};

type TargetSourceMappingRecord = ViewTargetMapping & ViewSourceTargetMapping;

export type ViewTargetInfo = {
    source_entity_id: number;
    target_entity_id: number | null;
    target_entity_table_constructor: string;
    target_entity_name: string;
    target_entity_status?: string | null;
    target_entity_dm_id?: number | null;
    target_entity_dm_status?: string | null;
    target_entity_rdv_id?: number | null;
    target_entity_rdv_status?: string | null;
    target_entity_bdv_id?: number | null;
    target_entity_bdv_status?: string | null;
    target_entity_mapping: ViewTargetMapping[];
    source_target_entity_mapping: ViewSourceTargetMapping[];
};

type JobTaskTargetMappingMeta = {
    jobTaskTargetEntityMappings: Record<string, TargetSourceMappingRecord[]>;
    uniqueTargetEntityMappings: ViewTargetMapping[];
    uniqueSourceTargetEntityMappings: ViewSourceTargetMapping[];
};

let jobTaskTargetLookupCache: Record<string, TargetSourceMappingRecord[]> = {};

const setJobTaskTargetLookupCache = (lookup: Record<string, TargetSourceMappingRecord[]>) => {
    jobTaskTargetLookupCache = lookup ?? {};
};

const collectSourceTargetMappings = (
    items: any[],
    fallbackLookup: Record<string, TargetSourceMappingRecord[]> = {}
): JobTaskTargetMappingMeta => {
    const mappingByTarget: Record<string, TargetSourceMappingRecord[]> = {};
    const uniqueTargetMappingMap = new Map<string, ViewTargetMapping>();
    const uniqueSourceTargetMappingMap = new Map<string, ViewSourceTargetMapping>();

    const normalizeId = (value: number | string | null | undefined) => {
        if (value == null) return null;
        if (typeof value === "number") return value;
        const parsed = parseInt(value, 10);
        return Number.isNaN(parsed) ? null : parsed;
    };

    const addMapping = (
        targetKey: string,
        mappingId: number | string | null | undefined,
        mappingStatus: string | null | undefined,
        sourceMappingId?: number | string | null | undefined,
        sourceMappingStatus?: string | null | undefined
    ) => {
        const normalizedId = normalizeId(mappingId);
        const normalizedStatus = mappingStatus ?? null;
        const normalizedSourceId = normalizeId(sourceMappingId);
        const normalizedSourceStatus = sourceMappingStatus ?? null;
        const hasTargetData = normalizedId !== null || normalizedStatus !== null;
        const hasSourceData = normalizedSourceId !== null || normalizedSourceStatus !== null;

        if (!hasTargetData && !hasSourceData) return;

        if (!mappingByTarget[targetKey]) {
            mappingByTarget[targetKey] = [];
        }

        const exists = mappingByTarget[targetKey].some((entry) => {
            const targetMatches =
                entry.target_entity_mapping_id === normalizedId &&
                entry.target_entity_mapping_status === normalizedStatus;
            if (!targetMatches) return false;

            if (!hasSourceData) {
                return true;
            }

            return (
                entry.source_target_entity_mapping_id === normalizedSourceId &&
                entry.source_target_entity_mapping_status === normalizedSourceStatus
            );
        });
        if (!exists) {
            mappingByTarget[targetKey].push({
                target_entity_mapping_id: normalizedId,
                target_entity_mapping_status: normalizedStatus,
                source_target_entity_mapping_id: normalizedSourceId,
                source_target_entity_mapping_status: normalizedSourceStatus,
            });
        }

        if (hasTargetData) {
            const targetUniqueKey = `${normalizedId ?? "null"}::${normalizedStatus ?? "null"}`;
            if (!uniqueTargetMappingMap.has(targetUniqueKey)) {
                uniqueTargetMappingMap.set(targetUniqueKey, {
                    target_entity_mapping_id: normalizedId,
                    target_entity_mapping_status: normalizedStatus,
                });
            }
        }

        if (hasSourceData) {
            const sourceUniqueKey = `${normalizedSourceId ?? "null"}::${normalizedSourceStatus ?? "null"}`;
            if (!uniqueSourceTargetMappingMap.has(sourceUniqueKey)) {
                uniqueSourceTargetMappingMap.set(sourceUniqueKey, {
                    source_target_entity_mapping_id: normalizedSourceId,
                    source_target_entity_mapping_status: normalizedSourceStatus,
                });
            }
        }
    };

    const resolveTargetKey = (item: any): string | null => {
        const identifier = item?.target_entity_id ?? item?.job_task_target_entity_id;
        return identifier != null ? String(identifier) : null;
    };

    items.forEach((item) => {
        const normalizedTargetKey = resolveTargetKey(item);
        if (!normalizedTargetKey) {
            return;
        }

        addMapping(
            normalizedTargetKey,
            item?.target_entity_mapping_id,
            item?.target_entity_mapping_status,
            item?.source_target_entity_mapping_id,
            item?.source_target_entity_mapping_status
        );

        const fallbackMappings = fallbackLookup[normalizedTargetKey];
        if (Array.isArray(fallbackMappings)) {
            fallbackMappings.forEach((mapping) =>
                addMapping(
                    normalizedTargetKey,
                    mapping.target_entity_mapping_id,
                    mapping.target_entity_mapping_status,
                    mapping.source_target_entity_mapping_id,
                    mapping.source_target_entity_mapping_status
                )
            );
        }
    });

    return {
        jobTaskTargetEntityMappings: mappingByTarget,
        uniqueTargetEntityMappings: Array.from(uniqueTargetMappingMap.values()),
        uniqueSourceTargetEntityMappings: Array.from(uniqueSourceTargetMappingMap.values()),
    };
};

const buildJobTaskTargetEntityLookup = (jobTakNode: any): Record<string, TargetSourceMappingRecord[]> => {
    const lookup: Record<string, TargetSourceMappingRecord[]> = {};

    if (!jobTakNode || typeof jobTakNode !== "object") {
        return lookup;
    }

    const normalizeId = (value: number | string | null | undefined) => {
        if (value == null) return null;
        if (typeof value === "number") return value;
        const parsed = parseInt(value, 10);
        return Number.isNaN(parsed) ? null : parsed;
    };

    const addEntry = (
        key: string,
        mappingId: number | string | null | undefined,
        mappingStatus: string | null | undefined,
        sourceMappingId?: number | string | null | undefined,
        sourceMappingStatus?: string | null | undefined
    ) => {
        const normalizedId = normalizeId(mappingId);
        const normalizedStatus = mappingStatus ?? null;
        const normalizedSourceId = normalizeId(sourceMappingId);
        const normalizedSourceStatus = sourceMappingStatus ?? null;
        const hasTargetData = normalizedId !== null || normalizedStatus !== null;
        const hasSourceData = normalizedSourceId !== null || normalizedSourceStatus !== null;

        if (!hasTargetData && !hasSourceData) return;

        if (!lookup[key]) {
            lookup[key] = [];
        }

        const exists = lookup[key].some((entry) => {
            const targetMatches =
                entry.target_entity_mapping_id === normalizedId &&
                entry.target_entity_mapping_status === normalizedStatus;
            if (!targetMatches) return false;

            if (!hasSourceData) {
                return true;
            }

            return (
                entry.source_target_entity_mapping_id === normalizedSourceId &&
                entry.source_target_entity_mapping_status === normalizedSourceStatus
            );
        });
        if (!exists) {
            lookup[key].push({
                target_entity_mapping_id: normalizedId,
                target_entity_mapping_status: normalizedStatus,
                source_target_entity_mapping_id: normalizedSourceId,
                source_target_entity_mapping_status: normalizedSourceStatus,
            });
        }
    };

    Object.entries(jobTakNode).forEach(([key, value]) => {
        if (!Array.isArray(value)) return;

        value.forEach((entry: any) => {
            const targetKey = entry?.target_entity_id != null ? String(entry.target_entity_id) : key;
            addEntry(
                targetKey,
                entry?.target_entity_mapping_id,
                entry?.target_entity_mapping_status,
                entry?.source_target_entity_mapping_id,
                entry?.source_target_entity_mapping_status
            );
        });
    });

    return lookup;
};

const extractTargetInfo = (
    taskGroups: JobTaskVersion2[],
    initialView: InitialViewItem[]
): ViewTargetInfo[] => {
    const NO_CHANGE = "NO_CHANGE";
    const resultsByKey = new Map<string, ViewTargetInfo>();

    const normalizeId = (value: any): number | null => {
        if (value == null || value === "") return null;
        if (typeof value === "number") return Number.isNaN(value) ? null : value;
        const parsed = parseInt(value, 10);
        return Number.isNaN(parsed) ? null : parsed;
    };

    const isChangedStatus = (status: any): boolean =>
        status != null && String(status).toUpperCase() !== NO_CHANGE;

    const pickPreferredStatus = (...statuses: any[]): string | null => {
        const nonNull = statuses.filter((s) => s != null);
        const changed = nonNull.find((s) => isChangedStatus(s));
        return changed ?? nonNull[0] ?? null;
    };

    const addUniqueMappings = (
        entry: ViewTargetInfo,
        source: InitialViewItem | undefined
    ) => {
        if (!source) return;

        const targetMappingId = normalizeId(source?.target_entity_mapping_id);
        const targetMappingStatus = source?.target_entity_mapping_status ?? null;
        if (targetMappingId !== null || targetMappingStatus !== null) {
            const exists = entry.target_entity_mapping.some(
                (m) =>
                    m.target_entity_mapping_id === targetMappingId &&
                    m.target_entity_mapping_status === targetMappingStatus
            );
            if (!exists) {
                entry.target_entity_mapping.push({
                    target_entity_mapping_id: targetMappingId,
                    target_entity_mapping_status: targetMappingStatus,
                });
            }
        }

        const sourceMappingId = normalizeId(source?.source_target_entity_mapping_id);
        const sourceMappingStatus = source?.source_target_entity_mapping_status ?? null;
        if (sourceMappingId !== null || sourceMappingStatus !== null) {
            const exists = entry.source_target_entity_mapping.some(
                (m) =>
                    m.source_target_entity_mapping_id === sourceMappingId &&
                    m.source_target_entity_mapping_status === sourceMappingStatus
            );
            if (!exists) {
                entry.source_target_entity_mapping.push({
                    source_target_entity_mapping_id: sourceMappingId,
                    source_target_entity_mapping_status: sourceMappingStatus,
                });
            }
        }
    };

    const upsertTarget = (
        payload: {
            source_entity_id: number;
            target_entity_id: number | null;
            target_entity_table_constructor: string;
            target_entity_name: string;
            target_entity_status?: string | null;
            target_entity_dm_id?: number | null;
            target_entity_dm_status?: string | null;
            target_entity_rdv_id?: number | null;
            target_entity_rdv_status?: string | null;
            target_entity_bdv_id?: number | null;
            target_entity_bdv_status?: string | null;
        },
        mappingSource?: InitialViewItem
    ) => {
        const key = [
            payload.target_entity_name ?? "",
            payload.target_entity_table_constructor ?? "",
            payload.target_entity_id ?? "null",
            payload.target_entity_rdv_id ?? "null",
            payload.target_entity_dm_id ?? "null",
        ].join("::");

        if (!resultsByKey.has(key)) {
            resultsByKey.set(key, {
                source_entity_id: payload.source_entity_id,
                target_entity_id: payload.target_entity_id ?? null,
                target_entity_table_constructor: payload.target_entity_table_constructor,
                target_entity_name: payload.target_entity_name,
                target_entity_status: payload.target_entity_status ?? null,
                target_entity_dm_id: payload.target_entity_dm_id ?? null,
                target_entity_dm_status: payload.target_entity_dm_status ?? null,
                target_entity_rdv_id: payload.target_entity_rdv_id ?? null,
                target_entity_rdv_status: payload.target_entity_rdv_status ?? null,
                target_entity_bdv_id: payload.target_entity_bdv_id ?? null,
                target_entity_bdv_status: payload.target_entity_bdv_status ?? null,
                target_entity_mapping: [],
                source_target_entity_mapping: [],
            });
        }

        const current = resultsByKey.get(key)!;
        current.target_entity_status = pickPreferredStatus(
            current.target_entity_status,
            payload.target_entity_status
        );
        current.target_entity_rdv_status = pickPreferredStatus(
            current.target_entity_rdv_status,
            payload.target_entity_rdv_status
        );
        current.target_entity_dm_status = pickPreferredStatus(
            current.target_entity_dm_status,
            payload.target_entity_dm_status
        );
        current.target_entity_bdv_status = pickPreferredStatus(
            current.target_entity_bdv_status,
            payload.target_entity_bdv_status
        );

        if (current.target_entity_id == null && payload.target_entity_id != null) {
            current.target_entity_id = payload.target_entity_id;
        }
        if (current.target_entity_rdv_id == null && payload.target_entity_rdv_id != null) {
            current.target_entity_rdv_id = payload.target_entity_rdv_id;
        }
        if (current.target_entity_dm_id == null && payload.target_entity_dm_id != null) {
            current.target_entity_dm_id = payload.target_entity_dm_id;
        }
        if (current.target_entity_bdv_id == null && payload.target_entity_bdv_id != null) {
            current.target_entity_bdv_id = payload.target_entity_bdv_id;
        }
        if (current.source_entity_id == null || current.source_entity_id < 0) {
            current.source_entity_id = payload.source_entity_id;
        }

        addUniqueMappings(current, mappingSource);
    };

    // lookup for legacy path (source_entitys join)
    const sourceEntityLookup = new Map<number, InitialViewItem[]>();
    for (const iv of initialView) {
        if (!sourceEntityLookup.has(iv.source_entity_id)) {
            sourceEntityLookup.set(iv.source_entity_id, []);
        }
        sourceEntityLookup.get(iv.source_entity_id)!.push(iv);
    }

    for (const tg of taskGroups) {
        const constructorValue =
            typeof tg?.target_entity_table_constructor === "string"
                ? tg.target_entity_table_constructor
                : "";
        const isViewTarget = constructorValue.toLowerCase().includes("asview");

        // New payload path: prefer task-level rdv/dm fields so values are not downgraded by old rows.
        if (constructorValue && isViewTarget) {
            const rdvFromCollection = Array.isArray(tg?.target_entity_rdvs)
                ? tg.target_entity_rdvs[0]
                : null;
            const dmFromCollection = Array.isArray(tg?.target_entity_dms)
                ? tg.target_entity_dms[0]
                : null;

            const rdvId =
                normalizeId(rdvFromCollection?.target_entity_rdv_id) ??
                normalizeId(tg?.target_entity_rdv_id);
            const dmId =
                normalizeId(dmFromCollection?.target_entity_dm_id) ??
                normalizeId(tg?.target_entity_dm_id);
            const targetEntityId =
                normalizeId(tg?.job_task_target_entity_id) ??
                normalizeId(tg?.target_entity_id);
            const targetName =
                tg?.target_entity_name ?? tg?.job_task_name ?? "Unknown Target";
            const sourceEntityId = normalizeId(tg?.source_entity_id) ?? -1;
            const rdvStatus = pickPreferredStatus(
                tg?.target_entity_rdv_status,
                rdvFromCollection?.target_entity_rdv_status
            );
            const dmStatus = pickPreferredStatus(
                tg?.target_entity_dm_status,
                dmFromCollection?.target_entity_dm_status
            );

            const matchedByTarget = initialView.filter((iv) => {
                if (String(iv?.target_entity_table_constructor ?? "") !== constructorValue) {
                    return false;
                }
                const ivTargetId = normalizeId(iv?.target_entity_id);
                const ivRdvId = normalizeId(iv?.target_entity_rdv_id);
                const ivDmId = normalizeId(iv?.target_entity_dm_id);
                return (
                    (targetEntityId != null && ivTargetId === targetEntityId) ||
                    (rdvId != null && ivRdvId === rdvId) ||
                    (dmId != null && ivDmId === dmId)
                );
            });

            if (matchedByTarget.length > 0) {
                for (const iv of matchedByTarget) {
                    upsertTarget(
                        {
                            source_entity_id: sourceEntityId >= 0 ? sourceEntityId : iv.source_entity_id,
                            target_entity_id: normalizeId(iv?.target_entity_id) ?? targetEntityId,
                            target_entity_table_constructor: constructorValue,
                            target_entity_name: targetName ?? iv?.target_entity_name ?? "Unknown Target",
                            target_entity_status: pickPreferredStatus(
                                tg?.target_entity_status,
                                iv?.target_entity_status
                            ),
                            target_entity_dm_id: dmId ?? normalizeId(iv?.target_entity_dm_id),
                            target_entity_dm_status: pickPreferredStatus(dmStatus, iv?.target_entity_dm_status),
                            target_entity_rdv_id: rdvId ?? normalizeId(iv?.target_entity_rdv_id),
                            target_entity_rdv_status: pickPreferredStatus(rdvStatus, iv?.target_entity_rdv_status),
                            target_entity_bdv_id: normalizeId(iv?.target_entity_bdv_id),
                            target_entity_bdv_status: iv?.target_entity_bdv_status ?? null,
                        },
                        iv
                    );
                }
            } else {
                upsertTarget({
                    source_entity_id: sourceEntityId,
                    target_entity_id: targetEntityId,
                    target_entity_table_constructor: constructorValue,
                    target_entity_name: targetName,
                    target_entity_status: tg?.target_entity_status ?? null,
                    target_entity_dm_id: dmId,
                    target_entity_dm_status: dmStatus,
                    target_entity_rdv_id: rdvId,
                    target_entity_rdv_status: rdvStatus,
                    target_entity_bdv_id: normalizeId(tg?.target_entity_bdv_id),
                    target_entity_bdv_status: tg?.target_entity_bdv_status ?? null,
                });
            }
        }

        // Legacy path: retain compatibility when only source_entitys are available.
        if (!Array.isArray(tg.source_entitys)) continue;
        for (const src of tg.source_entitys) {
            const matchingViewItems = sourceEntityLookup.get(src.source_entity_id);
            if (!Array.isArray(matchingViewItems)) continue;

            for (const iv of matchingViewItems) {
                if (!String(iv?.target_entity_table_constructor ?? "").toLowerCase().includes("asview")) {
                    continue;
                }
                upsertTarget(
                    {
                        source_entity_id: src.source_entity_id,
                        target_entity_id: normalizeId(iv?.target_entity_id),
                        target_entity_table_constructor: iv.target_entity_table_constructor,
                        target_entity_name: iv.target_entity_name,
                        target_entity_status: iv?.target_entity_status ?? null,
                        target_entity_dm_id: normalizeId(iv?.target_entity_dm_id),
                        target_entity_dm_status: iv?.target_entity_dm_status ?? null,
                        target_entity_rdv_id: normalizeId(iv?.target_entity_rdv_id),
                        target_entity_rdv_status: iv?.target_entity_rdv_status ?? null,
                        target_entity_bdv_id: normalizeId(iv?.target_entity_bdv_id),
                        target_entity_bdv_status: iv?.target_entity_bdv_status ?? null,
                    },
                    iv
                );
            }
        }
    }

    return Array.from(resultsByKey.values());
};

const flattenObjectToArray = (data: any): any[] => {
    const jobRecords: any[] = [];
    const sourceEntityRecords: any[] = [];
    const sourceTargetRecords: any[] = [];
    const dataSourceRecords: any[] = [];

    Object.entries(data).forEach(([key, value]) => {
        if (key === "job_and_task_pk_job_id_pk_source_entity_id" && value && typeof value === "object") {
            Object.values(value).forEach((arr: any) => {
                if (Array.isArray(arr)) jobRecords.push(...arr);
            });
        } else if (key === "source_entity_fk_source_entity_id_pk_data_source_id" && value && typeof value === "object") {
            Object.values(value).forEach((arr: any) => {
                if (Array.isArray(arr)) sourceEntityRecords.push(...arr);
            });
        } else if (key === "job_tak_target_entity_fk_target_entity_id" && value && typeof value === "object") {
            Object.values(value).forEach((arr: any) => {
                if (Array.isArray(arr)) sourceTargetRecords.push(...arr);
            });
        } else if (key === "data_source_fk_data_source_id" && value && typeof value === "object") {
            Object.values(value).forEach((arr: any) => {
                if (Array.isArray(arr)) dataSourceRecords.push(...arr);
            });
        }
    });

    const flatArray: any[] = [];

    jobRecords.forEach((job) => {
        const joinSourceEntityKey = job.job_task_source_entity_id;
        const joinTargetEntityKey = job.job_task_target_entity_id;

        const matchingSourceEntities = sourceEntityRecords.filter(
            (se) => String(se.source_entity_id) === String(joinSourceEntityKey)
        );

        if (matchingSourceEntities.length > 0) {
            matchingSourceEntities.forEach(sourceEntity => {
                const matchingDataSources = dataSourceRecords.filter(
                    (ds) => ds.data_source_id === sourceEntity.data_source_id
                );

                const matchingSourceTargets = sourceTargetRecords.filter(
                    (st) => String(st.target_entity_id) === String(joinTargetEntityKey) // ✅ UPDATED JOIN LOGIC
                );

                if (matchingSourceTargets.length > 0) {
                    matchingSourceTargets.forEach(st => {
                        if (matchingDataSources.length > 0) {
                            matchingDataSources.forEach(ds => {
                                flatArray.push({
                                    ...job,
                                    ...sourceEntity,
                                    ...ds,
                                    ...st,
                                });
                            });
                        } else {
                            flatArray.push({
                                ...job,
                                ...sourceEntity,
                                ...st,
                            });
                        }
                    });
                } else if (matchingDataSources.length > 0) {
                    matchingDataSources.forEach(ds => {
                        flatArray.push({
                            ...job,
                            ...sourceEntity,
                            ...ds,
                        });
                    });
                } else {
                    flatArray.push({
                        ...job,
                        ...sourceEntity,
                    });
                }
            });
        } else {
            flatArray.push({
                ...job,
            });
        }
    });

    return flatArray;
};

function extractAsViewTargets(data: any): {
    source_entity_id: number | null;
    target_entity_id: number | null;
    target_entity_name: string | null;
    target_entity_table_constructor: string | null;
    source_target_entity_mapping_id: number;
    source_target_entity_mapping_status: string;
    target_entity_dm_id?: number;
    target_entity_dm_status?: string;
    target_entity_rdv_id?: number;
    target_entity_rdv_status?: string;
    target_entity_bdv_id?: number;
    target_entity_bdv_status?: string;
    target_entity_mapping_id?: number;
    target_entity_mapping_status?: string;

}[] {
    const results: any[] = [];

    function recurse(node: any) {
        if (Array.isArray(node)) {
            node.forEach(recurse);
        } else if (node && typeof node === 'object') {
            const tec = node.target_entity_table_constructor;
            if (typeof tec === 'string' && tec.includes('AsView')) {
                results.push({
                    source_entity_id: node.source_entity_id ?? null,
                    target_entity_id: node.target_entity_id ?? null,
                    target_entity_name: node.target_entity_name ?? null,
                    target_entity_table_constructor: tec ?? null,
                    target_entity_status: node.target_entity_status ?? null,
                    source_target_entity_mapping_id: node.source_target_entity_mapping_id,
                    source_target_entity_mapping_status: node.source_target_entity_mapping_status,
                    target_entity_dm_id: node?.target_entity_dm_id ?? null,
                    target_entity_dm_status: node?.target_entity_dm_status ?? null,
                    target_entity_rdv_id: node?.target_entity_rdv_id ?? null,
                    target_entity_rdv_status: node?.target_entity_rdv_status ?? null,
                    target_entity_bdv_id: node?.target_entity_bdv_id ?? null,
                    target_entity_bdv_status: node?.target_entity_bdv_status ?? null,
                    target_entity_mapping_id: node?.target_entity_mapping_id ?? null,
                    target_entity_mapping_status: node?.target_entity_mapping_status ?? null
                });
            }
            Object.values(node).forEach(recurse);
        }
    }

    if (
        data &&
        typeof data === 'object' &&
        data.job_tak_target_entity_fk_target_entity_id
    ) {
        recurse(data.job_tak_target_entity_fk_target_entity_id);
    }

    return results;
}

export function createDynamicMappings(tableNames: string[]): DynamicMappingConfig[] {

    const columnDetailsGroup = new Set([
        "source_entity_column_new",
        "source_entity_column_updated",
        "source_entity_column_deleted",
        "source_entity_column_details_new",
        "source_entity_column_details_updated",
        "source_entity_column_details_deleted",
    ]);

    let hasDetailsGroup = false;
    const columnDetailGroupTableName: string[] | null = [];
    const mappings: DynamicMappingConfig[] = [];

    for (const tableName of tableNames) {
        if (columnDetailsGroup.has(tableName)) {

            hasDetailsGroup = true;

            columnDetailGroupTableName.push(tableName);

            continue;
        }


        let idField = `${tableName}_id`;
        let nameField = `${tableName}_name`;
        let statusField = `${tableName}_status`;

        if (tableName === "source_entity_column_new") {
            idField = `${tableName}_ids`;
            statusField = "source_entity_column_status_new";
        }

        const propertyName = tableName.endsWith('s') ? tableName : tableName + 's';

        mappings.push({
            tableName,
            propertyName,
            fields: {
                id: idField,
                name: nameField,
                status: statusField,
            },
        });
    }

    if (hasDetailsGroup) {

        for (const item of columnDetailGroupTableName) {
            const index = item.lastIndexOf('_');
            mappings.push({
                tableName: `${item}`,
                propertyName: `${item}`, // already plural
                fields: {
                    id: `${item}_ids`,
                    name: `${item}_name`,
                    status: item.substring(0, index) + "_status" + item.substring(index),
                },
            });
        }
    }

    return mappings;
}

function addDynamicMappingsToTask(task: any, item: any, mappings: DynamicMappingConfig[]) {
    mappings.forEach(mapping => {
        const idValue = item[mapping.fields.id];
        if (idValue == null || idValue === '') return;

        if (!task[mapping.propertyName]) {
            task[mapping.propertyName] = [];
        }

        const exists = task[mapping.propertyName].some((entry: any) => entry[mapping.fields.id] === idValue);
        if (!exists) {
            const newEntry: any = {};
            newEntry[mapping.fields.id] = idValue;
            if (mapping.fields.name) {
                newEntry[mapping.fields.name] = item[mapping.fields.name];
            }
            newEntry[mapping.fields.status] = item[mapping.fields.status];

            if (mapping.tableName === 'data_source') {
                newEntry['source_entity_id'] = item['source_entity_id'];
                newEntry['source_entity_name'] = item['source_entity_name'];
                newEntry['source_entity_version_id'] = item['source_entity_version_id'];
                newEntry['source_entity_version_status'] = item['source_entity_version_status'];
                newEntry['source_entity_status'] = item['source_entity_status'];
                newEntry['source_entity_delta_id'] = item['source_entity_delta_id'];
                newEntry['source_entity_delta_status'] = item['source_entity_delta_status'];
            }

            if (mapping.default) {
                Object.assign(newEntry, mapping.default);
            }

            task[mapping.propertyName].push(newEntry);
        }
    });
}

const NO_CHANGE_STATUS = "NO_CHANGE";
const TASK_TARGET_STATUS_KEYS = [
    "target_entity_rdv_status",
    "target_entity_dm_status",
    "target_entity_rdv_column_status",
    "target_entity_dm_column_status",
];

const TARGET_COLUMN_STATUS_KEYS = [
    "target_entity_rdv_column_status",
    "target_entity_dm_column_status",
];

function isChangedStatusValue(status: any): boolean {
    if (status == null) return false;
    return String(status).toUpperCase() !== NO_CHANGE_STATUS;
}

function mergeTaskTargetStatuses(task: any, item: any): void {
    TASK_TARGET_STATUS_KEYS.forEach((statusKey) => {
        const incomingStatus = item?.[statusKey];
        if (incomingStatus == null) return;

        const existingStatus = task?.[statusKey];
        if (existingStatus == null || !isChangedStatusValue(existingStatus)) {
            task[statusKey] = incomingStatus;
            return;
        }

        if (isChangedStatusValue(incomingStatus)) {
            task[statusKey] = incomingStatus;
        }
    });
}

function mergeTaskTargetColumnReasonKeys(task: any, item: any): void {
    const changedKeys = TARGET_COLUMN_STATUS_KEYS.filter((statusKey) =>
        isChangedStatusValue(item?.[statusKey])
    );

    if (changedKeys.length === 0) return;

    task._targetColumnPruneReasonKeys = Array.from(
        new Set([...(task._targetColumnPruneReasonKeys ?? []), ...changedKeys])
    );
}


const transformDataToNested = (flatData: any, dynamicMappings: DynamicMappingConfig[]) => {
    if (!Array.isArray(flatData)) {
        flatData = flattenObjectToArray(flatData);

    }

    if (typeof flatData === 'string') {
        try {
            flatData = JSON.parse(flatData);
        } catch (error) {
            console.error("Failed to parse flatData:", error);
            return [];
        }
    }


    const jobsMap: { [key: number]: JobVersion2 } = {};

    flatData.forEach((item: any) => {
        const {
            job_id,
            job_name,
            job_status,
            job_task_group_id,
            job_task_group_name,
            job_task_group_layer_id,
            layer_name,
            job_task_group_status,
            job_task_id,
            job_task_name,
            job_task_target_entity_id,
            job_task_target_entity_status,
            target_entity_table_constructor,
            job_task_status,
            job_dependency_id,
            job_dependency_status,
            job_task_group_dependency_id,
            job_task_group_dependency_status,
            job_task_dependency_id,
            job_task_dependency_status,
            job_step_name,
            change_status_job_step,
            source_entity_name,
            source_entity_status,
            data_source_name
        } = item;

        if (!jobsMap[job_id]) {
            jobsMap[job_id] = {
                job_id,
                job_name,
                selected: false,
                job_task_groups: [],
                job_dependency: [],
                job_status,
            };
        }
        const job = jobsMap[job_id];

        if (job_dependency_id || job_dependency_status) {
            if (!job.job_dependency.some((jd: any) => jd.job_dependency_id === job_dependency_id)) {
                job.job_dependency.push({
                    job_dependency_id,
                    job_dependency_status,
                });
            }
        }


        let taskGroup = job.job_task_groups.find((tg: any) => tg.job_task_group_id === job_task_group_id);
        if (!taskGroup) {
            taskGroup = {
                job_task_group_id,
                job_task_group_name,
                job_task_group_layer_id,
                layer_name,
                selected: false,
                job_tasks: [],
                job_task_group_dependency: [],
                job_task_group_status
            };
            job.job_task_groups.push(taskGroup);
        }

        if (job_task_group_dependency_id || job_task_group_dependency_status) {
            job.job_task_groups.forEach((taskGroup: any) => {
                const dependency = taskGroup.job_task_group_dependency.find(
                    (dep: any) => dep.job_task_group_dependency_id === job_task_group_dependency_id
                );

                if (!dependency) {
                    taskGroup.job_task_group_dependency.push(
                        {
                            job_task_group_dependency_id,
                            job_task_group_dependency_status
                        }
                    );
                }
            });
        }

        let task = taskGroup.job_tasks.find((t: any) => t.job_task_id === job_task_id);
        if (!task) {
            task = {
                job_task_id,
                job_task_name,
                job_task_status,
                selected: false,
                job_steps: [],
                // Dynamic arrays for other entities will be added below
            };

            task = {
                ...task,
                ...(job_task_target_entity_id != null
                    ? { job_task_target_entity_id }
                    : {}),
                ...(job_task_target_entity_status != null
                    ? { job_task_target_entity_status }
                    : {}),
                ...(target_entity_table_constructor != null
                    ? { target_entity_table_constructor }
                    : {}),
            };
            taskGroup.job_tasks.push(task);
        }

        if (job_task_dependency_id || job_task_dependency_status) {
            if (!task.job_task_dependency) task.job_task_dependency = [];
            if (!task.job_task_dependency.some((jtd: any) => jtd.job_task_dependency_id === job_task_dependency_id)) {
                task.job_task_dependency.push({
                    job_task_dependency_id,
                    job_task_dependency_status,
                });
            }
        }

        // if (job_step_name || change_status_job_step) {
        //     if (!task.job_steps.some((step: any) => step.job_step_name === job_step_name)) {
        //         task.job_steps.push({
        //             job_step_name,
        //             change_status_job_step,
        //             source_entity_name: item.source_entity_name,
        //             data_source_name: item.data_source_name,
        //         });
        //     }
        // }

        if (job_step_name ||
            change_status_job_step ||
            source_entity_name ||
            source_entity_status ||
            data_source_name) {
            const existingStep = task.job_steps.find(step => step.job_step_name === job_step_name);
            if (!existingStep) {
                task.job_steps.push({
                    job_step_name,
                    change_status_job_step,
                    source_entity_name,
                    source_entity_status,
                    data_source_name,
                    selected: false
                });
            }
        }

        addDynamicMappingsToTask(task, item, dynamicMappings);
        mergeTaskTargetStatuses(task, item);
        mergeTaskTargetColumnReasonKeys(task, item);
    });

    return Object.values(jobsMap);
};

const flattenNestedHierarchy = (data: any): any[] => {
    const flatList: any[] = [];
    if (data && data.job_and_task_pk_job_id_pk_source_entity_id) {
        const jobsObj = data.job_and_task_pk_job_id_pk_source_entity_id;
        for (const key in jobsObj) {
            if (Array.isArray(jobsObj[key])) {
                for (const job of jobsObj[key]) {

                    const { job_task_groups, ...jobLevel } = job;
                    if (Array.isArray(job_task_groups) && job_task_groups.length > 0) {
                        for (const tg of job_task_groups) {

                            const { job_tasks, ...tgLevel } = tg;
                            if (Array.isArray(job_tasks) && job_tasks.length > 0) {
                                for (const task of job_tasks) {

                                    const merged = { ...jobLevel, ...tgLevel, ...task };
                                    flatList.push(merged);
                                }
                            } else {

                                flatList.push({ ...jobLevel, ...tgLevel });
                            }
                        }
                    } else {

                        flatList.push({ ...jobLevel });
                    }
                }
            }
        }
    }
    return flatList;
};

const filterListByHierarchy = (data: any, extractedHierarchy: any) => {

    let rawTargetLookup: Record<string, TargetSourceMappingRecord[]> = jobTaskTargetLookupCache;

    if (typeof data === "string") {
        try {
            data = JSON.parse(data);
        } catch (error) {
            console.error("Failed to parse data:", error);
            return [];
        }
    }

    if (
        data &&
        typeof data === "object" &&
        !Array.isArray(data) &&
        data.job_and_task_pk_job_id_pk_source_entity_id
    ) {
        rawTargetLookup = buildJobTaskTargetEntityLookup(data.job_tak_target_entity_fk_target_entity_id);
        setJobTaskTargetLookupCache(rawTargetLookup);
        data = flattenNestedHierarchy(data);
    }

    if (!Array.isArray(data)) {
        return [];
    }
    if ((!rawTargetLookup || Object.keys(rawTargetLookup).length === 0) && (data as any).__job_tak_lookup) {
        rawTargetLookup = (data as any).__job_tak_lookup;
    }

    const filteredItems = data.filter((item: any) => {

        const matchedJob = extractedHierarchy?.find((job: any) => job?.job_id === item?.job_id);
        if (!matchedJob) return false;


        if (Array.isArray(matchedJob.job_task_groups) && matchedJob.job_task_groups.length > 0) {
            const matchedTG = matchedJob.job_task_groups.find(
                (tg: any) => tg?.job_task_group_id === item?.job_task_group_id
            );

            if (!matchedTG) return item?.job_task_group_id === null;


            if (Array.isArray(matchedTG.job_tasks) && matchedTG.job_tasks.length > 0) {
                const matchedTask = matchedTG.job_tasks.find(
                    (task: any) => task?.job_task_id === item?.job_task_id
                );
                return !!matchedTask || item?.job_task_id === null;
            } else {

                return true;
            }
        } else {

            return true;
        }
    });

    const mappingMeta = collectSourceTargetMappings(filteredItems, rawTargetLookup);
    filteredItems.forEach((item: any) => {
        const identifier = item?.target_entity_id ?? item?.job_task_target_entity_id;
        const combinedMappings = identifier != null
            ? mappingMeta.jobTaskTargetEntityMappings[String(identifier)]
            : undefined;

        const targetSeen = new Set<string>();
        const sourceSeen = new Set<string>();

        const targetMappings: ViewTargetMapping[] = [];
        const sourceMappings: ViewSourceTargetMapping[] = [];

        if (Array.isArray(combinedMappings)) {
            combinedMappings.forEach((mapping) => {
                const targetKey = `${mapping.target_entity_mapping_id ?? "null"}::${mapping.target_entity_mapping_status ?? "null"}`;
                if (!targetSeen.has(targetKey)) {
                    targetSeen.add(targetKey);
                    if (mapping.target_entity_mapping_id !== null || mapping.target_entity_mapping_status !== null) {
                        targetMappings.push({
                            target_entity_mapping_id: mapping.target_entity_mapping_id,
                            target_entity_mapping_status: mapping.target_entity_mapping_status,
                        });
                    }
                }

                const sourceKey = `${mapping.source_target_entity_mapping_id ?? "null"}::${mapping.source_target_entity_mapping_status ?? "null"}`;
                if (
                    !sourceSeen.has(sourceKey) &&
                    (mapping.source_target_entity_mapping_id !== null || mapping.source_target_entity_mapping_status !== null)
                ) {
                    sourceSeen.add(sourceKey);
                    sourceMappings.push({
                        source_target_entity_mapping_id: mapping.source_target_entity_mapping_id,
                        source_target_entity_mapping_status: mapping.source_target_entity_mapping_status,
                    });
                }
            });
        }

        item.target_entity_mapping = targetMappings;
        item.source_target_entity_mapping = sourceMappings;
    });

    Object.defineProperty(filteredItems, "unique_target_entity_mappings", {
        value: mappingMeta.uniqueTargetEntityMappings,
        enumerable: false,
    });
    Object.defineProperty(filteredItems, "unique_source_target_entity_mappings", {
        value: mappingMeta.uniqueSourceTargetEntityMappings,
        enumerable: false,
    });

    return filteredItems;
};

const extractJobIdsHierarchy = (list: any) => {
    return list?.map((job: any) => {
        const jobId = job.job_id;

        const jobTaskGroups = job?.job_task_groups?.map((taskGroup: any) => {
            const jobTaskGroupId = taskGroup.job_task_group_id;

            const jobTasks = taskGroup?.job_tasks?.map((task: any) => {
                return {
                    job_task_id: task.job_task_id
                };
            });

            return {
                job_task_group_id: jobTaskGroupId,
                job_tasks: jobTasks
            };
        });

        return {
            job_id: jobId,
            job_task_groups: jobTaskGroups
        };
    });
};

const saveJobData = (envName: string, jobData: any, packageId: number | string) => {

    let storedArray = JSON.parse(localStorage.getItem('jobsArray')) || [];


    const existingIndex = storedArray.findIndex((item: any) => item.envName === envName);

    if (existingIndex !== -1) {
        storedArray[existingIndex].jobData = jobData;
    } else {
        storedArray.push({ envName: envName, jobData: jobData, [envName]: packageId });
    }

    localStorage.setItem('jobsArray', JSON.stringify(storedArray));
};

const statusMapping = (status: string | null | undefined) => {
    switch (status) {
        case "NEW":
            return 1;
        case "UPDATED":
            return 2;
        case "DELETED":
            return 3;
        case "NO_CHANGE":
            return 0;
        default:
            return null;
    }
};

export const createDynamicFields = (tableNames: string[]): FieldMapping[] => {

    const specialOverrides: Record<string, { statusSuffix: string }> = {
        // job_task_group_dependency: { statusSuffix: "target_status" },
        source_entity_column: { statusSuffix: "change_status_source_entity_column" },
        source_entity_column_details: { statusSuffix: "change_status_source_entity_column_details" },
        source_entity_version: { statusSuffix: "change_status_source_entity_version" },
    };

    return tableNames.map((tableName) => {
        const idKey = `${tableName}_id`;
        let statusKey = `${tableName}_status`;
        if (specialOverrides[tableName]) {
            statusKey = `${tableName}_${specialOverrides[tableName].statusSuffix}`;
        }
        return { idKey, statusKey };
    });
};

const isEmptyObject = (obj: any) => {
    const x = obj &&
        typeof obj === 'object' &&
        !Array.isArray(obj) &&
        Object.keys(obj).length === 0
    return x
}

const transformListToBackendSchema = (feObjectsList: any, deployment_pkg_id: any, tableNames: string[], views: ViewTargetInfo[]) => {
    const fields = createDynamicFields(tableNames);

    fields.push({
        idKey: "job_task_target_entity_id",
        statusKey: "job_task_target_entity_status"
    });

    const deploymentPkgInfoList = feObjectsList.flatMap((feObject: any) => {
        return fields
            .map(({ idKey, statusKey, }) => {
                if (feObject[idKey]) {
                    return {
                        deployment_pkg_id: deployment_pkg_id,
                        table_id_column_name: idKey,
                        table_id_column_id: parseInt(feObject[idKey]),
                        change_status: statusMapping(feObject[statusKey]),
                        created_at: new Date().toISOString(),
                        job_id: parseInt(feObject["job_id"]),
                        source_entity_column_new_ids:
                            feObject &&
                                feObject.hasOwnProperty("source_entity_id") &&
                                feObject.source_entity_id !== null &&
                                feObject.source_entity_id !== undefined &&
                                feObject.source_entity_id !== "" &&
                                idKey === "source_entity_id" &&
                                !isEmptyObject(feObject["source_entity_column_new_ids"])
                                ? (feObject["source_entity_column_new_ids"] ?? null)
                                : null,
                        source_entity_column_updated_ids:
                            feObject &&
                                feObject.hasOwnProperty("source_entity_id") &&
                                feObject.source_entity_id !== null &&
                                feObject.source_entity_id !== undefined &&
                                feObject.source_entity_id !== "" &&
                                idKey === "source_entity_id" &&
                                !isEmptyObject(feObject["source_entity_column_updated_ids"])
                                ? (feObject["source_entity_column_updated_ids"] ?? null)
                                : null,
                        source_entity_column_deleted_ids:
                            feObject &&
                                feObject.hasOwnProperty("source_entity_id") &&
                                feObject.source_entity_id !== null &&
                                feObject.source_entity_id !== undefined &&
                                feObject.source_entity_id !== "" &&
                                idKey === "source_entity_id" &&
                                !isEmptyObject(feObject["source_entity_column_deleted_ids"])
                                ? (feObject["source_entity_column_deleted_ids"] ?? null)
                                : null,
                        source_entity_column_details_new_ids:
                            feObject &&
                                feObject.hasOwnProperty("source_entity_id") &&
                                feObject.source_entity_id !== null &&
                                feObject.source_entity_id !== undefined &&
                                feObject.source_entity_id !== "" &&
                                idKey === "source_entity_id" &&
                                !isEmptyObject(feObject["source_entity_column_details_new_ids"])
                                ? (feObject["source_entity_column_details_new_ids"] ?? null)
                                : null,
                        source_entity_column_details_updated_ids:
                            feObject &&
                                feObject.hasOwnProperty("source_entity_id") &&
                                feObject.source_entity_id !== null &&
                                feObject.source_entity_id !== undefined &&
                                feObject.source_entity_id !== "" &&
                                idKey === "source_entity_id" &&
                                !isEmptyObject(feObject["source_entity_column_details_updated_ids"])
                                ? (feObject["source_entity_column_details_updated_ids"] ?? null)
                                : null,
                        source_entity_column_details_deleted_ids:
                            feObject &&
                                feObject.hasOwnProperty("source_entity_id") &&
                                feObject.source_entity_id !== null &&
                                feObject.source_entity_id !== undefined &&
                                feObject.source_entity_id !== "" &&
                                idKey === "source_entity_id" &&
                                !isEmptyObject(feObject["source_entity_column_details_deleted_ids"])
                                ? (feObject["source_entity_column_details_deleted_ids"] ?? null)
                                : null,
                    };
                }
                return null;
            })
            .filter((item) => {
                return item !== null
            });

    });

    const uniqueDeploymentPkgInfoList = Array.from(
        new Set(
            deploymentPkgInfoList.map((item: any) => {
                const { created_at, ...rest } = item;
                return JSON.stringify(rest);
            })
        )
    ).map((itemStr: any) => {
        const obj = JSON.parse(itemStr);
        obj.created_at = new Date().toISOString();
        return obj;
    });

    if (views?.length > 0) {
        views.forEach((vt) => {
            uniqueDeploymentPkgInfoList.push({
                deployment_pkg_id: deployment_pkg_id,
                table_id_column_name: "view_target_entity_id",
                table_id_column_id: vt.target_entity_id ?? null,
                created_at: new Date().toISOString(),
                job_id: null,
                change_status: statusMapping(vt.target_entity_status) ?? undefined,
            });

            const buildPkgInfo = (id: number | null | undefined, status: string | null | undefined, columnName: string) => ({
                deployment_pkg_id: deployment_pkg_id,
                table_id_column_name: columnName,
                table_id_column_id: id ?? null,
                created_at: new Date().toISOString(),
                // @ts-ignore
                job_id: null,
                change_status: statusMapping(status) ?? undefined,
            });

            vt.target_entity_mapping.forEach((mapping) => {
                uniqueDeploymentPkgInfoList.push(
                    buildPkgInfo(mapping.target_entity_mapping_id, mapping.target_entity_mapping_status, "target_entity_mapping_id")
                );
            });

            vt.source_target_entity_mapping.forEach((mapping) => {
                uniqueDeploymentPkgInfoList.push(
                    buildPkgInfo(mapping.source_target_entity_mapping_id, mapping.source_target_entity_mapping_status, "source_target_entity_mapping_id")
                );
            });

            // Conditionally push for DM
            if (vt?.target_entity_dm_id) {
                uniqueDeploymentPkgInfoList.push(
                    buildPkgInfo(vt.target_entity_dm_id, vt.target_entity_dm_status, "target_entity_dm_id")
                );
            }

            // Conditionally push for RDV
            if (vt?.target_entity_rdv_id) {
                uniqueDeploymentPkgInfoList.push(
                    buildPkgInfo(vt.target_entity_rdv_id, vt.target_entity_rdv_status, "target_entity_rdv_id")
                );
            }

            // Conditionally push for BDV
            if (vt?.target_entity_bdv_id) {
                uniqueDeploymentPkgInfoList.push(
                    buildPkgInfo(vt.target_entity_bdv_id, vt.target_entity_bdv_status, "target_entity_bdv_id")
                );
            }
        });
    }

    feObjectsList?.forEach((feObject: any) => {
        const jobId =
            feObject?.job_id != null && feObject?.job_id !== ""
                ? parseInt(String(feObject.job_id), 10)
                : null;

        const mappings = feObject?.target_entity_mapping;
        const sourceMappings = feObject?.source_target_entity_mapping;

        if (Array.isArray(mappings) && mappings.length > 0) {
            mappings.forEach((m: any) => {
                const mappingIdRaw = m?.target_entity_mapping_id;
                const mappingId =
                    mappingIdRaw == null || mappingIdRaw === ""
                        ? null
                        : parseInt(String(mappingIdRaw), 10);

                if (mappingId == null || Number.isNaN(mappingId)) return;

                uniqueDeploymentPkgInfoList.push({
                    deployment_pkg_id: deployment_pkg_id,
                    table_id_column_name: "target_entity_mapping_id",
                    table_id_column_id: mappingId,
                    change_status: statusMapping(m?.target_entity_mapping_status),
                    created_at: new Date().toISOString(),
                    job_id: jobId,
                    source_entity_column_new_ids:
                        feObject &&
                            feObject.hasOwnProperty("source_entity_id") &&
                            feObject.source_entity_id !== null &&
                            feObject.source_entity_id !== undefined &&
                            feObject.source_entity_id !== "" &&
                            // idKey === "source_entity_id" &&
                            !isEmptyObject(feObject["source_entity_column_new_ids"])
                            ? (feObject["source_entity_column_new_ids"] ?? null)
                            : null,
                    source_entity_column_updated_ids:
                        feObject &&
                            feObject.hasOwnProperty("source_entity_id") &&
                            feObject.source_entity_id !== null &&
                            feObject.source_entity_id !== undefined &&
                            feObject.source_entity_id !== "" &&
                            // idKey === "source_entity_id" &&
                            !isEmptyObject(feObject["source_entity_column_updated_ids"])
                            ? (feObject["source_entity_column_updated_ids"] ?? null)
                            : null,
                    source_entity_column_deleted_ids:
                        feObject &&
                            feObject.hasOwnProperty("source_entity_id") &&
                            feObject.source_entity_id !== null &&
                            feObject.source_entity_id !== undefined &&
                            feObject.source_entity_id !== "" &&
                            // idKey === "source_entity_id" &&
                            !isEmptyObject(feObject["source_entity_column_deleted_ids"])
                            ? (feObject["source_entity_column_deleted_ids"] ?? null)
                            : null,
                    source_entity_column_details_new_ids:
                        feObject &&
                            feObject.hasOwnProperty("source_entity_id") &&
                            feObject.source_entity_id !== null &&
                            feObject.source_entity_id !== undefined &&
                            feObject.source_entity_id !== "" &&
                            // idKey === "source_entity_id" &&
                            !isEmptyObject(feObject["source_entity_column_details_new_ids"])
                            ? (feObject["source_entity_column_details_new_ids"] ?? null)
                            : null,
                    source_entity_column_details_updated_ids:
                        feObject &&
                            feObject.hasOwnProperty("source_entity_id") &&
                            feObject.source_entity_id !== null &&
                            feObject.source_entity_id !== undefined &&
                            feObject.source_entity_id !== "" &&
                            // idKey === "source_entity_id" &&
                            !isEmptyObject(feObject["source_entity_column_details_updated_ids"])
                            ? (feObject["source_entity_column_details_updated_ids"] ?? null)
                            : null,
                    source_entity_column_details_deleted_ids:
                        feObject &&
                            feObject.hasOwnProperty("source_entity_id") &&
                            feObject.source_entity_id !== null &&
                            feObject.source_entity_id !== undefined &&
                            feObject.source_entity_id !== "" &&
                            // idKey === "source_entity_id" &&
                            !isEmptyObject(feObject["source_entity_column_details_deleted_ids"])
                            ? (feObject["source_entity_column_details_deleted_ids"] ?? null)
                            : null,
                });
            });
        }

        if (Array.isArray(sourceMappings) && sourceMappings.length > 0) {
            sourceMappings.forEach((m: any) => {
                const mappingIdRaw = m?.source_target_entity_mapping_id;
                const mappingId =
                    mappingIdRaw == null || mappingIdRaw === ""
                        ? null
                        : parseInt(String(mappingIdRaw), 10);

                if (mappingId == null || Number.isNaN(mappingId)) return;

                uniqueDeploymentPkgInfoList.push({
                    deployment_pkg_id: deployment_pkg_id,
                    table_id_column_name: "source_target_entity_mapping_id",
                    table_id_column_id: mappingId,
                    change_status: statusMapping(m?.source_target_entity_mapping_status),
                    created_at: new Date().toISOString(),
                    job_id: jobId,
                    source_entity_column_new_ids:
                        feObject &&
                            feObject.hasOwnProperty("source_entity_id") &&
                            feObject.source_entity_id !== null &&
                            feObject.source_entity_id !== undefined &&
                            feObject.source_entity_id !== "" &&
                            // idKey === "source_entity_id" &&
                            !isEmptyObject(feObject["source_entity_column_new_ids"])
                            ? (feObject["source_entity_column_new_ids"] ?? null)
                            : null,
                    source_entity_column_updated_ids:
                        feObject &&
                            feObject.hasOwnProperty("source_entity_id") &&
                            feObject.source_entity_id !== null &&
                            feObject.source_entity_id !== undefined &&
                            feObject.source_entity_id !== "" &&
                            // idKey === "source_entity_id" &&
                            !isEmptyObject(feObject["source_entity_column_updated_ids"])
                            ? (feObject["source_entity_column_updated_ids"] ?? null)
                            : null,
                    source_entity_column_deleted_ids:
                        feObject &&
                            feObject.hasOwnProperty("source_entity_id") &&
                            feObject.source_entity_id !== null &&
                            feObject.source_entity_id !== undefined &&
                            feObject.source_entity_id !== "" &&
                            // idKey === "source_entity_id" &&
                            !isEmptyObject(feObject["source_entity_column_deleted_ids"])
                            ? (feObject["source_entity_column_deleted_ids"] ?? null)
                            : null,
                    source_entity_column_details_new_ids:
                        feObject &&
                            feObject.hasOwnProperty("source_entity_id") &&
                            feObject.source_entity_id !== null &&
                            feObject.source_entity_id !== undefined &&
                            feObject.source_entity_id !== "" &&
                            // idKey === "source_entity_id" &&
                            !isEmptyObject(feObject["source_entity_column_details_new_ids"])
                            ? (feObject["source_entity_column_details_new_ids"] ?? null)
                            : null,
                    source_entity_column_details_updated_ids:
                        feObject &&
                            feObject.hasOwnProperty("source_entity_id") &&
                            feObject.source_entity_id !== null &&
                            feObject.source_entity_id !== undefined &&
                            feObject.source_entity_id !== "" &&
                            // idKey === "source_entity_id" &&
                            !isEmptyObject(feObject["source_entity_column_details_updated_ids"])
                            ? (feObject["source_entity_column_details_updated_ids"] ?? null)
                            : null,
                    source_entity_column_details_deleted_ids:
                        feObject &&
                            feObject.hasOwnProperty("source_entity_id") &&
                            feObject.source_entity_id !== null &&
                            feObject.source_entity_id !== undefined &&
                            feObject.source_entity_id !== "" &&
                            // idKey === "source_entity_id" &&
                            !isEmptyObject(feObject["source_entity_column_details_deleted_ids"])
                            ? (feObject["source_entity_column_details_deleted_ids"] ?? null)
                            : null,
                });
            });
        }
    });

    return uniqueDeploymentPkgInfoList;
};



export const filterSelectedItems = (jobs: JobVersion2[], tableNames: string[]): Job[] =>
    (jobs ?? [])
        .map((job) => {
            if (
                !job.selected &&
                !job.job_task_groups.some(
                    (tg) =>
                        tg?.selected || (tg.job_tasks ?? []).some((task) => task?.selected)
                )
            ) {
                return null;
            }

            const filteredTaskGroups = (job.job_task_groups ?? [])
                .map((taskGroup) => {
                    if (
                        !taskGroup.selected &&
                        !(taskGroup.job_tasks ?? []).some((task) => task?.selected)
                    ) {
                        return null;
                    }

                    const filteredTasks = (taskGroup.job_tasks ?? [])
                        .map((task) => {
                            const isTaskSelected =
                                task?.selected ||
                                (task.data_sources ?? []).some((ds: any) => ds?.selected) ||
                                (task.custom_plugins ?? []).some((cp: any) => cp?.selected) ||
                                (task.target_entities ?? []).some((te: any) => te?.selected);

                            if (!isTaskSelected) return null;

                            const updatedTask = { ...task };

                            tableNames.forEach((tableName) => {
                                const propertyName = tableName.endsWith("s")
                                    ? tableName
                                    : tableName + "s";

                                if (Array.isArray(updatedTask[propertyName])) {
                                    const statusKey = `${tableName}_status`;
                                    const arr = updatedTask[propertyName];
                                    const predicate =
                                        arr.length > 0 && Object.prototype.hasOwnProperty.call(arr[0], "selected")
                                            ? (item: any) => Boolean(item.selected)
                                            : (item: any) => item[statusKey] !== null;

                                    updatedTask[propertyName] = arr.filter(predicate);
                                }
                            });

                            return updatedTask;
                        })
                        .filter(Boolean);

                    return { ...taskGroup, job_tasks: filteredTasks };
                })
                .filter(Boolean);

            return { ...job, job_task_groups: filteredTaskGroups };
        })
        .filter(Boolean) as Job[];

export const useMetaDataDeployment = () => {
    const { tokens } = useAuth();
    const authHeaders = tokens.accessToken ? { Authorization: `Bearer ${tokens.accessToken}` } : {};

    const [jobs, setJobs] = useState<JobVersion2[]>([]);
    const [initialJobList, setInitialJobList] = useState([])
    const [expandedItems, setExpandedItems] = useState<Set<number | string>>(new Set());
    const [selectedHierarchy, setSelectedHierarchy] = useState<JobVersion2[]>([]);
    const [filteredItems, setFilteredItems] = useState([])
    const [packageId, setPackageId] = useState("")
    const [droppedJobs, setDroppedJobs] = useState([null, null, null]);
    const [draggedElement, setDraggedElement] = useState(null);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [selectedTargets, setSelectedTargets] = useState("")
    const [targetEnvironmentId, setTargetEnvironmentId] = useState(null)
    useEffect(() => {
        const matchedEnv = environments.find(
            env => env.environment_type === selectedTargets
        );
        const targetEnvironmentId = matchedEnv ? matchedEnv.environment_id : null;
        setTargetEnvironmentId(targetEnvironmentId)
    }, [selectedTargets])
    const [packageDeploymentExecution, setPackageDeploymentExecution] = useState<string[] | any>([])
    const [showProgress, setShowProgress] = useState<boolean>(false)
    const [jobToShowProgressFor, setJobsToShowProgressFor] = useState<any>(null)
    const [environments, setEnvironments] = useState<Environment[]>([]);
    const [showInitialSelectionModalLoader, setShowInitialSelectionModalLoader] = useState(false);
    const [tableNames, setTableNames] = useState<string[]>()

    const [selectedTargetEntities, setSelectedTargetEntities] = useState<Set<string>>(new Set());
    const [selectedViewTargets, setSelectedViewTargets] = useState<ViewTargetInfo[]>([]);

    const [initialView, setInitialView] = useState<InitialViewItem[]>([])

    const [childEntityUpdate, setChildEntityUpdate] = useState(false)

    const resetAllStates = () => {
        setJobs([]);
        setInitialJobList([]);
        setExpandedItems(new Set());
        setSelectedHierarchy([]);
        setFilteredItems([]);
        setPackageId("");
        setDroppedJobs([null, null, null]);
        setDraggedElement(null);
        setPosition({ x: 0, y: 0 });
        setIsDragging(false);
        setSelectedTargets("");
        // setTargetEnvironmentId(null);
        setPackageDeploymentExecution([]);
        setShowProgress(false);
        setJobsToShowProgressFor(null);
        // setEnvironments([]);
        setShowInitialSelectionModalLoader(false);
        // setTableNames(undefined);
        setSelectedTargetEntities(new Set());
        setSelectedViewTargets([]);
        setInitialView([]);
    };

    const fetchDynamicMappings = async (): Promise<DynamicMappingConfig[]> => {
        const customer_id = localStorage.getItem("customer_id")
        const environment_id = localStorage.getItem("environment_id")
        const response = await fetch(`${process.env.REACT_APP_API_URL}environments/${environment_id}/table-names?customer_id=${customer_id}&environment_id=${environment_id}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                ...authHeaders,
            }
        });

        if (!response.ok) {
            const errorRes = await response.json();
            throw new Error(errorRes.detail || 'Error fetching table names');
        }

        const tableNames: string[] = await response.json();
        setTableNames(tableNames)
        return createDynamicMappings(tableNames);
    };

    useEffect(() => {
        if (!tokens.accessToken) {
            return;
        }

        const jobsProgress = JSON.parse(localStorage.getItem("jobsArray"))
        const jobDataArray = jobsProgress?.map((item: {
            envName: string;
            jobData: string;
        }) => item?.jobData);

        const flattenedJobDataArray = jobDataArray?.flat();

        if (flattenedJobDataArray?.length > 0) {
            setShowProgress(true)
            setJobsToShowProgressFor(flattenedJobDataArray)
        }

        const fetchTableNames = async () => {
            await fetchDynamicMappings();
        }
        fetchTableNames()

        getEnvironments()?.then((res) => {
            setEnvironments(res)
        });
    }, [tokens.accessToken])

    const [hasColumnChangeReason, setHasColumnChangeReason] = useState(false);
    const [hasColumnChangeReasonTaskName, setHasColumnChangeReasonTaskName] = useState([])

    const COLUMN_KEYS = ["source_entity_column_new", "source_entity_column_updated", "source_entity_column_deleted"];
    const DETAIL_KEYS = ["source_entity_column_details_new", "source_entity_column_details_updated", "source_entity_column_details_deleted"];

    // const CHANGE_VALUES = new Set(["NEW", "UPDATED", "DELETED"]); // anything not NO_CHANGE
    const NO_CHANGE = "NO_CHANGE";

    function isChangedStatus(val: any): boolean {
        if (val == null) return false;
        const s = String(val).toUpperCase();
        if (s === NO_CHANGE) return false;
        // treat any non-NO_CHANGE as change. If you only want the 3 listed, use CHANGE_VALUES.has(s)
        return true;
    }

    // Check a task for inner-most changes you care about
    function taskHasInnermostChange(task: any): boolean {
        if (!task || typeof task !== "object") return false;

        // 1) target entity status sitting on task
        if (isChangedStatus(task.target_entity_rdv_status)) return true;
        if (isChangedStatus(task.target_entity_dm_status)) return true;

        // 1.1) target entity status fields arriving directly on the task payload
        for (const key of TASK_TARGET_STATUS_KEYS) {
            if (isChangedStatus(task?.[key])) return true;
        }

        // 1.2) target entity status fields sitting in dynamic arrays
        const targetEntityCollections = [
            { key: "target_entity_rdvs", statusKey: "target_entity_rdv_status" },
            { key: "target_entity_dms", statusKey: "target_entity_dm_status" },
            { key: "target_entity_rdv_columns", statusKey: "target_entity_rdv_column_status" },
            { key: "target_entity_dm_columns", statusKey: "target_entity_dm_column_status" },
        ];
        for (const collection of targetEntityCollections) {
            if (Array.isArray(task?.[collection.key])) {
                for (const entity of task[collection.key]) {
                    if (isChangedStatus(entity?.[collection.statusKey])) return true;
                }
            }
        }

        // 2) nested source entities
        if (Array.isArray(task.source_entitys)) {
            for (const se of task.source_entitys) {
                if (isChangedStatus(se?.source_entity_status)) return true;
            }
        }

        // 3) nested custom plugins
        if (Array.isArray(task.custom_plugins)) {
            for (const cp of task.custom_plugins) {
                if (isChangedStatus(cp?.custom_plugin_status)) return true;
            }
        }

        return false;
    }

    function getTargetColumnStatusChangeKeys(task: any): string[] {
        if (!task || typeof task !== "object") return [];

        const changedKeys = new Set<string>();

        if (Array.isArray(task?._targetColumnPruneReasonKeys)) {
            task._targetColumnPruneReasonKeys.forEach((key: string) => {
                if (TARGET_COLUMN_STATUS_KEYS.includes(key)) changedKeys.add(key);
            });
        }

        TARGET_COLUMN_STATUS_KEYS.forEach((key) => {
            if (isChangedStatus(task?.[key])) changedKeys.add(key);
        });

        const targetColumnCollections = [
            { key: "target_entity_rdv_columns", statusKey: "target_entity_rdv_column_status" },
            { key: "target_entity_dm_columns", statusKey: "target_entity_dm_column_status" },
        ];

        targetColumnCollections.forEach((collection) => {
            if (!Array.isArray(task?.[collection.key])) return;

            if (task[collection.key].some((entity: any) => isChangedStatus(entity?.[collection.statusKey]))) {
                changedKeys.add(collection.statusKey);
            }
        });

        return Array.from(changedKeys);
    }

    function taskHasInnermostChangeExcludingTargetColumns(task: any): boolean {
        if (!task || typeof task !== "object") return false;

        if (isChangedStatus(task.target_entity_rdv_status)) return true;
        if (isChangedStatus(task.target_entity_dm_status)) return true;

        const targetEntityCollections = [
            { key: "target_entity_rdvs", statusKey: "target_entity_rdv_status" },
            { key: "target_entity_dms", statusKey: "target_entity_dm_status" },
        ];
        for (const collection of targetEntityCollections) {
            if (Array.isArray(task?.[collection.key])) {
                for (const entity of task[collection.key]) {
                    if (isChangedStatus(entity?.[collection.statusKey])) return true;
                }
            }
        }

        if (Array.isArray(task.source_entitys)) {
            for (const se of task.source_entitys) {
                if (isChangedStatus(se?.source_entity_status)) return true;
            }
        }

        if (Array.isArray(task.custom_plugins)) {
            for (const cp of task.custom_plugins) {
                if (isChangedStatus(cp?.custom_plugin_status)) return true;
            }
        }

        return false;
    }

    function getTargetColumnPruneReasonKeys(task: any): string[] {
        const changedTargetColumnKeys = getTargetColumnStatusChangeKeys(task);
        if (changedTargetColumnKeys.length === 0) return [];

        const isNoChangeTask = String(task?.job_task_status).toUpperCase() === NO_CHANGE;
        if (!isNoChangeTask) return [];

        if (taskHasInnermostChangeExcludingTargetColumns(task)) return [];
        if (taskHasNonEmptySpecialBuckets(task)) return [];

        return changedTargetColumnKeys;
    }

    function decorateTargetColumnPruneReason(task: any): void {
        const reasonKeys = getTargetColumnPruneReasonKeys(task);
        if (reasonKeys.length === 0) return;

        task._targetColumnPruneReasonKeys = Array.from(
            new Set([...(task._targetColumnPruneReasonKeys ?? []), ...reasonKeys])
        );
    }
    // True if ANY special bucket has content
    function taskHasNonEmptySpecialBuckets(task: any): boolean {
        const columnsNonEmpty = COLUMN_KEYS.some(key =>
            Array.isArray(task?.[key]) &&
            task[key].some((item: any) => Array.isArray(item?.[`${key}_ids`]) && item[`${key}_ids`].length > 0)
        );

        const detailsNonEmpty = DETAIL_KEYS.some(key =>
            Array.isArray(task?.[key]) &&
            task[key].some((item: any) =>
                item?.[`${key}_ids`] &&
                typeof item[`${key}_ids`] === "object" &&
                Object.keys(item[`${key}_ids`]).length > 0
            )
        );

        return columnsNonEmpty || detailsNonEmpty;
    }

    function isTaskRemovable(task: any): boolean {
        // malformed?
        const malformed =
            !task ||
            task.job_task_id == null ||
            task.job_task_name == null ||
            task.job_task_status == null;

        if (malformed) return true;

        // If the task has inner-most changes => KEEP
        if (taskHasInnermostChange(task)) return false;

        // If special buckets have content => KEEP
        if (taskHasNonEmptySpecialBuckets(task)) return false;

        // Otherwise remove only if its own status is NO_CHANGE
        const isNoChange = String(task.job_task_status).toUpperCase() === NO_CHANGE;
        return isNoChange;
    }

    function collectColumnChangeTaskNames(obj: any, out: string[]) {
        if (!obj) return;
        if (Array.isArray(obj)) { obj.forEach(v => collectColumnChangeTaskNames(v, out)); return; }
        if (typeof obj === "object") {
            if (obj.job_task_name && taskHasNonEmptySpecialBuckets(obj)) out.push(obj.job_task_name);
            Object.values(obj).forEach(v => { if (typeof v === "object") collectColumnChangeTaskNames(v, out); });
        }
    }


    const [hasDependencyUpdate, setHasDependencyUpdate] = useState(false);
    const [hasTaskGroupDependencyUpdate, setHasTaskgroupDependencyUpdate] = useState(false);

    // NEW: which specific ones
    const [jobDependencyUpdateIds, setJobDependencyUpdateIds] = useState<number[]>([]);
    const [taskGroupDependencyUpdateIds, setTaskGroupDependencyUpdateIds] = useState<number[]>([]);


    function pruneOnlyTasksAndEmptyContainers(data: any[]): any[] {
        const cloned = JSON.parse(JSON.stringify(data)); // do not mutate input

        // drives setChildEntityUpdate – ONLY inner-most task changes
        let anyTaskHasChange = false;

        // drives global dependency booleans
        let anyDependencyChange = false;
        let anyTaskGroupDependencyChange = false;

        // NEW: collect exact IDs
        const jobIdsWithDepChange: number[] = [];
        const taskGroupIdsWithDepChange: number[] = [];

        // --- HELPERS: dependency-change detection -----------------

        const hasJobDependencyChange = (job: any): boolean => {
            const deps = job?.job_dependency;
            if (!Array.isArray(deps)) return false;

            return deps.some(
                (d: any) =>
                    d &&
                    typeof d.job_dependency_status === "string" &&
                    d.job_dependency_status.toUpperCase() !== "NO_CHANGE"
            );
        };

        const hasJobTaskGroupDependencyChange = (group: any): boolean => {
            const deps = group?.job_task_group_dependency;
            if (!Array.isArray(deps)) return false;

            return deps.some(
                (d: any) =>
                    d &&
                    typeof d.job_task_group_dependency_status === "string" &&
                    d.job_task_group_dependency_status.toUpperCase() !== "NO_CHANGE"
            );
        };

        // --------------------------------------------------------------

        for (const job of cloned) {
            const groups = job?.job_task_groups;
            if (!Array.isArray(groups)) continue;

            // Job-level dependency changes
            if (hasJobDependencyChange(job)) {
                anyDependencyChange = true;

                // associate with this job
                if (job.job_id != null) {
                    jobIdsWithDepChange.push(job.job_id);
                }
                // also decorate the object for convenient UI checks
                job._hasDependencyUpdate = true;
            }

            for (const group of groups) {
                if (Array.isArray(group?.job_tasks)) {
                    // existing task pruning logic
                    group.job_tasks = group.job_tasks.filter((task: any) => {
                        const removable = isTaskRemovable(task);

                        if (!removable && taskHasInnermostChange(task)) {
                            anyTaskHasChange = true;
                        }

                        if (!removable) {
                            decorateTargetColumnPruneReason(task);
                        }

                        return !removable;
                    });
                } else {
                    group.job_tasks = [];
                }

                // Group-level dependency changes
                if (hasJobTaskGroupDependencyChange(group)) {
                    anyTaskGroupDependencyChange = true;

                    if (group.job_task_group_id != null) {
                        taskGroupIdsWithDepChange.push(group.job_task_group_id);
                    }
                    group._hasDependencyUpdate = true;
                }
            }

            // Drop groups that ended up empty *unless* they have dependency changes
            job.job_task_groups = groups.filter((g: any) => {
                const hasTasks =
                    Array.isArray(g.job_tasks) && g.job_tasks.length > 0;
                const hasDepChange = hasJobTaskGroupDependencyChange(g);
                return hasTasks || hasDepChange;
            });
        }

        // Remove jobs with no remaining groups *unless* they have dependency changes
        const pruned = cloned.filter((job: any) => {
            const hasGroups =
                Array.isArray(job.job_task_groups) && job.job_task_groups.length > 0;
            const hasDepChange = hasJobDependencyChange(job);
            return hasGroups || hasDepChange;
        });

        // Your existing flags (column-change names)
        const names: string[] = [];
        collectColumnChangeTaskNames(pruned, names);
        setHasColumnChangeReason(names.length > 0);
        setHasColumnChangeReasonTaskName(Array.from(new Set(names)));

        // ORIGINAL: only inner-most task changes
        setChildEntityUpdate(anyTaskHasChange);

        // Global dependency info
        setHasDependencyUpdate(anyDependencyChange);
        setHasTaskgroupDependencyUpdate(anyTaskGroupDependencyChange);

        // NEW: per-entity association
        setJobDependencyUpdateIds(Array.from(new Set(jobIdsWithDepChange)));
        setTaskGroupDependencyUpdateIds(
            Array.from(new Set(taskGroupIdsWithDepChange))
        );

        return pruned;
    }

    const [loading, setLoading] = useState(false);
    const [emptyResultWarning, setEmptyResultWarning] = useState(false);

    useEffect(() => {
        if (selectedTargets === "" || !tokens.accessToken) {
            return;
        }
        setLoading(true)
        const matchedEnv = environments.find(
            env => env.environment_type === selectedTargets
        );
        const targetEnvironmentId = matchedEnv ? matchedEnv.environment_id : null;
        const sourceEnvironmentId = parseInt(localStorage.getItem("environment_id"))
        const customer_id = parseInt(localStorage.getItem("customer_id"))

        setShowInitialSelectionModalLoader(true);

        let dynamicMappings: DynamicMappingConfig[]


        const fetchData = async () => {
            try {

                const mappings = await fetchDynamicMappings();
                // const mappings = createDynamicMappings(tableNames);
                dynamicMappings = mappings;
                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}environments/${sourceEnvironmentId}/compare_environment_changes?customer_id=${customer_id}&src_environment_id=${sourceEnvironmentId}&trg_environment_id=${targetEnvironmentId}`,
                    {
                        method: "GET",
                        headers: {
                            "Content-Type": "application/json",
                            ...authHeaders,
                        },
                    }
                );

                if (!response.ok) {
                    throw new Error(`Error: ${response.statusText}`);
                }

                let finalData = await response.json();
                // const finalData = mockData;
                // finalData = {}
                if (typeof finalData === "object" && !Array.isArray(finalData) && Object.keys(finalData).length === 0) {
                    setEmptyResultWarning(true);
                }

                // const jobTakLookup = buildJobTaskTargetEntityLookup(finalData?.job_tak_target_entity_fk_target_entity_id);
                // setJobTaskTargetLookupCache(jobTakLookup);

                const flattenedJobList = flattenObjectToArray(finalData);
                // Object.defineProperty(flattenedJobList, "__job_tak_lookup", {
                //     value: jobTakLookup,
                //     enumerable: false,
                // });

                setInitialJobList(flattenedJobList);

                const asViewObjects = extractAsViewTargets(finalData);

                setInitialView(asViewObjects)

                const transformedData = transformDataToNested(finalData, dynamicMappings)

                // downloadJson(transformedData[0])

                const filteredList = pruneOnlyTasksAndEmptyContainers(transformedData);



                setJobs(filteredList);

                // setJobs(transformedData)
            } catch (error) {
                console.error("Error fetching data:", error);
            } finally {
                setShowInitialSelectionModalLoader(false);
                setLoading(false)
            }
        };

        fetchData();
    }, [selectedTargets, tokens.accessToken]);


    useEffect(() => {
        const selectedItems = filterSelectedItems(selectedHierarchy, tableNames);
        setFilteredItems(selectedItems)
    }, [selectedHierarchy]);


    const selectAllDescendants = (subitems: any[], selected: boolean): any[] => {
        return subitems.map(subitem => {
            const updatedItem = {
                ...subitem,
                selected,
                job_task_groups: subitem.job_task_groups
                    ? selectAllDescendants(subitem.job_task_groups, selected)
                    : [],
                job_tasks: subitem.job_tasks
                    ? selectAllDescendants(subitem.job_tasks, selected)
                    : [],
                data_sources: subitem.data_sources
                    ? subitem.data_sources.map((ds: any) => ({ ...ds, selected }))
                    : [],
                custom_plugins: subitem.custom_plugins
                    ? subitem.custom_plugins.map((cp: any) => ({ ...cp, selected }))
                    : [],
                target_entities: subitem.target_entities
                    ? subitem.target_entities.map((te: any) => ({ ...te, selected }))
                    : [],
                job_steps: subitem.job_steps
                    ? subitem.job_steps.map((js: any) => ({ ...js, selected }))
                    : [],
            };

            // collect related view target info and sync with selectedTargetEntities + selectedViewTargets
            const relatedTargets = extractTargetInfo([updatedItem], initialView);
            const updatedTargetSet = new Set(selectedTargetEntities);
            const updatedViewTargets = [...selectedViewTargets];

            for (const ti of relatedTargets) {
                const key = `${ti.target_entity_name}-${ti.target_entity_table_constructor}`;
                if (selected) {
                    updatedTargetSet.add(key);

                    const exists = updatedViewTargets.some(
                        v =>
                            v.target_entity_name === ti.target_entity_name &&
                            v.target_entity_table_constructor === ti.target_entity_table_constructor
                    );

                    if (!exists) {
                        updatedViewTargets.push({
                            ...ti,
                            target_entity_mapping: [...ti.target_entity_mapping],
                            source_target_entity_mapping: [...ti.source_target_entity_mapping],
                        });
                    }
                } else {
                    updatedTargetSet.delete(key);
                }
            }

            // update the outer state only once after all recursion ends
            setSelectedTargetEntities(updatedTargetSet);
            setSelectedViewTargets(updatedViewTargets);

            return updatedItem;
        });
    };

    const updateSelectedHierarchy = () => {
        const selected = jobs.filter(
            job =>
                job.selected ||
                job.job_task_groups.some(
                    tg => tg.selected || tg.job_tasks.some(task => task.selected)
                )
        );
        setSelectedHierarchy(selected);
    };

    useEffect(() => {
        updateSelectedHierarchy();
    }, [jobs]);

    const adjustOtherGroupsOnTier1NewDeselect = (jobs: JobVersion2[]) => {
        return jobs.map(job => ({
            ...job,
            job_task_groups: job.job_task_groups.map(tg => {
                if (tg.job_task_group_layer_id !== 1 || tg.job_task_group_status !== "NEW") {
                    return {
                        ...tg,
                        selected: false,
                        disabled: true,
                        job_tasks: tg.job_tasks.map(task => ({
                            ...task,
                            selected: false,
                            data_sources: task.data_sources?.map((ds: any) => ({ ...ds, selected: false })) ?? [],
                            custom_plugins: task.custom_plugins?.map((cp: any) => ({ ...cp, selected: false })) ?? [],
                            target_entities: task.target_entities?.map((te: any) => ({ ...te, selected: false })) ?? [],
                            job_steps: task.job_steps?.map(js => ({ ...js, selected: false })) ?? [],
                        })),
                    };
                }
                return { ...tg, disabled: false }; // Tier1 group should not be disabled
            }),
        }));
    };

    const [tier1NewCheckedJobs, setTier1NewCheckedJobs] = useState<{ [jobId: number]: boolean }>({});

    const areAllJobsSelected = (jobs: JobVersion2[]) => {
        return jobs.length > 0 && jobs.every(job => job.selected);
    };

    const handleSelectAllJobs = () => {
        const allSelected = areAllJobsSelected(jobs);
        const newSelectedState = !allSelected;

        const updatedJobs = jobs.map(job => {
            const updatedTaskGroups = job.job_task_groups.map(tg => ({
                ...tg,
                selected: newSelectedState,
                disabled: false,
                job_tasks: selectAllDescendants(tg.job_tasks, newSelectedState),
            }));

            const hasTier1New = updatedTaskGroups.some(
                tg => tg.job_task_group_layer_id === 1 && tg.job_task_group_status === "NEW"
            );

            if (hasTier1New) {
                setTier1NewCheckedJobs(prev => ({
                    ...prev,
                    [job.job_id]: newSelectedState
                }));
            }

            let updatedJob = {
                ...job,
                selected: newSelectedState,
                job_task_groups: updatedTaskGroups,
            };

            if (!newSelectedState) {
                updatedJob = adjustOtherGroupsOnTier1NewDeselect([updatedJob])[0];
            }

            return updatedJob;
        });

        setJobs(updatedJobs);
        setShowProgress(false);

        // 🔁 Apply same view target update logic
        const allSelectedTasks = updatedJobs
            .filter(job => job.selected)
            .flatMap(job =>
                job.job_task_groups.flatMap(tg =>
                    tg.job_tasks.filter(task => task.selected)
                )
            );

        const viewTargets = allSelectedTasks.flatMap(task =>
            extractTargetInfo([task], initialView)
        );

        const newSelectedSet = new Set<string>();
        const newViewList: ViewTargetInfo[] = [];

        for (const ti of viewTargets) {
            const key = `${ti.target_entity_name}-${ti.target_entity_table_constructor}`;
            newSelectedSet.add(key);

            const alreadyInList = newViewList.some(
                v =>
                    v.target_entity_name === ti.target_entity_name &&
                    v.target_entity_table_constructor === ti.target_entity_table_constructor
            );

            if (!alreadyInList) {
                newViewList.push({
                    ...ti,
                    target_entity_mapping: [...ti.target_entity_mapping],
                    source_target_entity_mapping: [...ti.source_target_entity_mapping],
                });
            }
        }

        setSelectedTargetEntities(newSelectedSet);
        setSelectedViewTargets(
            newViewList.filter(ti =>
                newSelectedSet.has(`${ti.target_entity_name}-${ti.target_entity_table_constructor}`)
            )
        );
    };

    const handleJobSelect = (jobId: number) => {
        let updatedJobs = jobs.map(job => {
            if (job.job_id === jobId) {
                const newSelectedState = !job.selected;

                const updatedTaskGroups = job.job_task_groups.map(tg => ({
                    ...tg,
                    selected: newSelectedState,
                    disabled: false,
                    job_tasks: selectAllDescendants(tg.job_tasks, newSelectedState),
                }));

                // Update “tier1 new checked” state
                const hasTier1New = updatedTaskGroups.some(
                    tg => tg.job_task_group_layer_id === 1 && tg.job_task_group_status === "NEW"
                );
                if (hasTier1New) {
                    // setIsTier1NewChecked(newSelectedState);
                    setTier1NewCheckedJobs(prev => ({
                        ...prev,
                        [jobId]: newSelectedState  // true or false based on selection
                    }));
                }

                let updatedJob = {
                    ...job,
                    selected: newSelectedState,
                    job_task_groups: updatedTaskGroups,
                };

                if (!newSelectedState) {
                    // If job-level deselection, everything disabled
                    updatedJob = adjustOtherGroupsOnTier1NewDeselect([updatedJob])[0];
                }

                return updatedJob;
            }
            return job;
        });

        setJobs(updatedJobs);
        setShowProgress(false);

        // 🔄 Collect all selected job tasks across all selected jobs
        const allSelectedTasks = updatedJobs
            .filter(job => job.selected)
            .flatMap(job =>
                job.job_task_groups.flatMap(tg =>
                    tg.job_tasks.filter(task => task.selected)
                )
            );

        // 🧠 Extract target info from all selected tasks
        const viewTargets = allSelectedTasks.flatMap(task =>
            extractTargetInfo([task], initialView)
        );

        // ✅ Build Set and View List
        const newSelectedSet = new Set<string>();
        const newViewList: ViewTargetInfo[] = [];

        for (const ti of viewTargets) {
            const key = `${ti.target_entity_name}-${ti.target_entity_table_constructor}`;
            newSelectedSet.add(key);

            const alreadyInList = newViewList.some(
                v =>
                    v.target_entity_name === ti.target_entity_name &&
                    v.target_entity_table_constructor === ti.target_entity_table_constructor
            );

            if (!alreadyInList) {
                newViewList.push({
                    ...ti,
                    target_entity_mapping: [...ti.target_entity_mapping],
                    source_target_entity_mapping: [...ti.source_target_entity_mapping],
                });
            }
        }

        // 🧠 Update global state
        setSelectedTargetEntities(newSelectedSet);
        setSelectedViewTargets(
            newViewList.filter(ti =>
                newSelectedSet.has(`${ti.target_entity_name}-${ti.target_entity_table_constructor}`)
            )
        );
    };

    useEffect(() => {
        setExpandedItems(prevExpanded => {
            const newExpandedItems = new Set(prevExpanded);

            Object.entries(tier1NewCheckedJobs).forEach(([jobIdStr, isTier1NewChecked]) => {
                const jobId = Number(jobIdStr);
                const job = jobs.find(j => j.job_id === jobId);
                if (!job) return;

                // If tier 1 with NEW status is not selected
                // if (!isTier1NewChecked) {
                //     job.job_task_groups.forEach(tg => {
                //         if (
                //             tg.job_task_group_layer_id !== 1 ||
                //             tg.job_task_group_status !== "NEW"
                //         ) {
                //             newExpandedItems.delete(tg.job_task_group_id);
                //         }
                //     });
                // }

                if (!isTier1NewChecked) {
                    const hasAnyTier1New = job.job_task_groups.some(
                        tg => tg.job_task_group_layer_id === 1 && tg.job_task_group_status === "NEW"
                    );

                    // Only collapse tier 2 and 3 groups if this job had any NEW tier 1 groups
                    if (hasAnyTier1New) {
                        job.job_task_groups.forEach(tg => {
                            if (tg.job_task_group_layer_id === 2 || tg.job_task_group_layer_id === 3) {
                                newExpandedItems.delete(tg.job_task_group_id);
                            }
                        });
                    }
                }

                const hasSelectedTier1 = job.job_task_groups.some(
                    tg => tg.job_task_group_layer_id === 1 && tg.selected
                );

                const hasSelectedTier2 = job.job_task_groups.some(
                    tg => tg.job_task_group_layer_id === 2 && tg.selected
                );

                const hasSelectedTier3 = job.job_task_groups.some(
                    tg => tg.job_task_group_layer_id === 3 && tg.selected
                );

                const nothingSelected = !hasSelectedTier1 && !hasSelectedTier2 && !hasSelectedTier3;

                if (!hasSelectedTier2 && !hasSelectedTier3 && !nothingSelected) {
                    const onlyTier3Exists = job.job_task_groups.every(
                        tg => tg.job_task_group_layer_id === 3
                    );

                    if (!onlyTier3Exists) {
                        job.job_task_groups.forEach(tg => {
                            if (tg.job_task_group_layer_id === 3) {
                                newExpandedItems.delete(tg.job_task_group_id);
                            }
                        });
                    }
                }
            });

            return newExpandedItems;
        });
    }, [tier1NewCheckedJobs]);


    const handleTaskGroupSelect = (jobId: number, taskGroupId: number) => {
        const job = jobs.find(j => j.job_id === jobId);
        if (!job) return;

        const currentGroup = job.job_task_groups.find(g => g.job_task_group_id === taskGroupId);
        if (!currentGroup) return;

        const newSelectedState = !currentGroup.selected;
        // const onlyTier3Exists = job.job_task_groups.every(g => g.job_task_group_layer_id === 3);
        const hasTier2 = job.job_task_groups.some(g => g.job_task_group_layer_id === 2);

        let updatedTaskGroups = job.job_task_groups.map(g => {
            if (g.job_task_group_id === taskGroupId) {
                return {
                    ...g,
                    selected: newSelectedState,
                    disabled: false,
                    job_tasks: selectAllDescendants(g.job_tasks, newSelectedState)
                };
            }
            return g;
        });

        // ✅ Tier1 deselected → disable and uncheck dependent Tier2 and Tier3
        if (
            currentGroup.job_task_group_layer_id === 1 &&
            currentGroup.job_task_group_status === "NEW" &&
            !newSelectedState
        ) {
            const noOtherTier1NewSelected = updatedTaskGroups.every(
                g =>
                    !(g.job_task_group_layer_id === 1 &&
                        g.job_task_group_status === "NEW" &&
                        g.selected)
            );

            if (noOtherTier1NewSelected) {
                updatedTaskGroups = updatedTaskGroups.map(g => {
                    if (g.job_task_group_layer_id === 2 || (g.job_task_group_layer_id === 3 && hasTier2)) {
                        return {
                            ...g,
                            selected: false,
                            disabled: true,
                            job_tasks: selectAllDescendants(g.job_tasks, false)
                        };
                    }
                    return g;
                });
            }
        }

        const shouldDeselectTier3 = currentGroup.job_task_group_layer_id === 2 && !newSelectedState;

        // ✅ Tier2 deselected → uncheck Tier3
        if (shouldDeselectTier3) {
            updatedTaskGroups = updatedTaskGroups.map(g => {
                if (g.job_task_group_layer_id === 3) {
                    return {
                        ...g,
                        selected: false,
                        disabled: true,
                        job_tasks: selectAllDescendants(g.job_tasks, false)
                    };
                }
                return g;
            });

            setExpandedItems(prevExpanded => {
                const nextExpanded = new Set(prevExpanded);
                updatedTaskGroups.forEach(g => {
                    if (g.job_task_group_layer_id !== 3) return;

                    nextExpanded.delete(g.job_task_group_id);
                    g.job_tasks?.forEach(task => nextExpanded.delete(task.job_task_id));
                });
                return nextExpanded;
            });
        }

        // ✅ If only Tier 3 exists or Tier 2 is absent → Tier 3 independent toggle already handled

        const isTier1NewStillSelected = updatedTaskGroups.some(
            g => g.job_task_group_layer_id === 1 &&
                g.job_task_group_status === "NEW" &&
                g.selected
        );
        setTier1NewCheckedJobs(prev => ({ ...prev, [jobId]: isTier1NewStillSelected }));

        // ✅ Recalculate job selection
        const jobHasSelection = updatedTaskGroups.some(
            g => g.selected || g.job_tasks.some(t => t.selected)
        );

        const updatedJob = {
            ...job,
            selected: jobHasSelection,
            job_task_groups: updatedTaskGroups
        };

        const updatedJobs = jobs.map(j => j.job_id === jobId ? updatedJob : j);
        setJobs(updatedJobs);

        // ✅ Update view targets for this group only
        const selectedGroup = updatedTaskGroups.find(g => g.job_task_group_id === taskGroupId);
        if (!selectedGroup) return;

        const viewSyncGroups = shouldDeselectTier3
            ? updatedTaskGroups.filter(
                g => g.job_task_group_id === taskGroupId || g.job_task_group_layer_id === 3
            )
            : [selectedGroup];

        const viewTargets = viewSyncGroups.flatMap(group =>
            group.job_tasks.flatMap(t => extractTargetInfo([t], initialView))
        );
        const newSelectedSet = new Set(selectedTargetEntities);
        const newViewList = [...selectedViewTargets];

        for (const ti of viewTargets) {
            const key = `${ti.target_entity_name}-${ti.target_entity_table_constructor}`;
            if (newSelectedState && selectedGroup.job_task_group_id === taskGroupId) {
                newSelectedSet.add(key);
                const already = newViewList.some(
                    v => v.target_entity_name === ti.target_entity_name &&
                        v.target_entity_table_constructor === ti.target_entity_table_constructor
                );
                if (!already) {
                    newViewList.push({ ...ti });
                }
            } else {
                newSelectedSet.delete(key);
            }
        }

        setSelectedTargetEntities(newSelectedSet);
        setSelectedViewTargets(
            newViewList.filter(ti =>
                newSelectedSet.has(`${ti.target_entity_name}-${ti.target_entity_table_constructor}`)
            )
        );
    };

    const handleJobTaskSelect = (jobId: number, taskGroupId: number, taskId: number) => {

        const updatedJobs = jobs.map(job => {
            if (job.job_id !== jobId) return job;

            const updatedTaskGroups = job.job_task_groups.map(tg => {
                if (tg.job_task_group_id !== taskGroupId) return tg;

                const updatedTasks = tg.job_tasks.map(task => {
                    if (task.job_task_id !== taskId) {
                        return task;
                    }

                    const newSelectedState = !task.selected;

                    return {
                        ...task,
                        selected: newSelectedState,
                        // @ts-ignore
                        data_sources: (task.data_sources ?? []).map(ds => ({ ...ds, selected: newSelectedState })),
                        // @ts-ignore
                        custom_plugins: (task.custom_plugins ?? []).map(cp => ({ ...cp, selected: newSelectedState })),
                        // @ts-ignore
                        target_entities: (task.target_entities ?? []).map(te => ({ ...te, selected: newSelectedState })),
                        job_steps: (task.job_steps ?? []).map(js => ({ ...js, selected: newSelectedState })),
                    };
                });

                const taskGroupSelected = updatedTasks.some(task => task.selected);

                return {
                    ...tg,
                    selected: taskGroupSelected,
                    job_tasks: updatedTasks
                };
            });

            // Check if *any* "layer 1 & NEW" group (or its tasks) are selected
            const isTier1NewSelected = updatedTaskGroups.some(
                tg =>
                    tg.job_task_group_layer_id === 1 &&
                    tg.job_task_group_status === "NEW" &&
                    (tg.selected || tg.job_tasks.some(task => task.selected))
            );

            const finalTaskGroups = updatedTaskGroups.map(tg => {
                if (tg.job_task_group_layer_id === 1 && tg.job_task_group_status === "NEW") {
                    return tg; // ✅ Always keep Tier1+NEW groups as-is
                }

                if (isTier1NewSelected) {
                    return tg; // ✅ If any Tier1+NEW is selected, allow all groups to remain active
                }

                // ❗ Check: is this the one being interacted with? (taskGroupId matches)
                const isThisClickedGroup = tg.job_task_group_id === taskGroupId;

                if (isThisClickedGroup) {
                    return tg; // ✅ Keep this group as-is (don’t deselect it)
                }

                // ❌ Otherwise, deselect and clear
                const clearedTasks = tg.job_tasks.map(task => ({
                    ...task,
                    selected: false,
                    data_sources: (task.data_sources ?? []).map((ds: any) => ({ ...ds, selected: false })),
                    custom_plugins: (task.custom_plugins ?? []).map((cp: any) => ({ ...cp, selected: false })),
                    target_entities: (task.target_entities ?? []).map((te: any) => ({ ...te, selected: false })),
                    job_steps: (task.job_steps ?? []).map(js => ({ ...js, selected: false })),
                }));

                return {
                    ...tg,
                    selected: false,
                    job_tasks: clearedTasks
                };
            });

            // ✅ Final job selection state depends on whether *any* taskGroup (or its tasks) is still selected
            const jobStillSelected = finalTaskGroups.some(tg => tg.selected);

            return {
                ...job,
                selected: jobStillSelected,
                job_task_groups: finalTaskGroups
            };
        });

        setJobs(updatedJobs);
        // setIsTier1NewChecked(updatedJobs.some(job =>
        //     job.job_task_groups.some(
        //         tg =>
        //             tg.job_task_group_layer_id === 1 &&
        //             tg.job_task_group_status === "NEW" &&
        //             (tg.selected || tg.job_tasks.some(task => task.selected))
        //     )
        // ));
        const newTier1NewCheckedJobs: { [jobId: number]: boolean } = {};
        updatedJobs.forEach(job => {
            const hasLayer1New = job.job_task_groups.some(
                tg =>
                    tg.job_task_group_layer_id === 1 &&
                    tg.job_task_group_status === "NEW" &&
                    (tg.selected || tg.job_tasks.some(task => task.selected))
            );
            newTier1NewCheckedJobs[job.job_id] = hasLayer1New;
        });
        setTier1NewCheckedJobs(newTier1NewCheckedJobs);
        setShowProgress(false);
    };


    const createDeploymentPkg = async (data: any) => {
        if (!data) {
            console.error("objectToSend is undefined!");
            throw new Error("Payload not set");
        }

        try {

            delete data.created_at;
            delete data.deployed_at;

            const customer_id = localStorage.getItem("customer_id")
            const environment_id = localStorage.getItem("environment_id")
            const response = await fetch(`${process.env.REACT_APP_API_URL}deployment_pkgs?customer_id=${customer_id}&environment_id=${environment_id}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...authHeaders,
                },
                body: JSON.stringify(data)
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.detail || "Error creating deployment package");
            }

            return result.deployment_pkg_id;
        } catch (error) {
            throw error;
        }
    };


    const createDeploymentPkgInfo = async (deploymentPkgInfoData: any, targetEnvironmentId: null | number) => {

        try {
            const customer_id = localStorage.getItem("customer_id")
            const environment_id = localStorage.getItem("environment_id")
            const response = await fetch(`${process.env.REACT_APP_API_URL}deployment_pkg_info?customer_id=${customer_id}&environment_id=${environment_id}&target_environment_id=${targetEnvironmentId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...authHeaders,
                },
                body: JSON.stringify(deploymentPkgInfoData)
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.detail || "Error creating deployment package info");
            }

        } catch (error) {
            console.error('Error:', error);
        }
    };

    const togglePanel = (id: string | number) => {
        setExpandedItems(prev => {
            const newExpandedItems = new Set(prev);
            if (newExpandedItems.has(id)) {
                newExpandedItems.delete(id);
            } else {
                newExpandedItems.add(id);
            }
            return newExpandedItems;
        });
    };

    const checkDropPosition = (x: any, y: any) => {
        const dropZones = document.querySelectorAll('.drop-zone');
        let isDropped = false;

        dropZones.forEach((zone, index) => {
            const rect = zone.getBoundingClientRect();
            if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) {
                isDropped = true;

                setDroppedJobs((prevJobs) => {

                    if (!prevJobs[index]) {
                        const updatedJobs = [...prevJobs];
                        updatedJobs[index] = draggedElement;
                        return updatedJobs;
                    }
                    return prevJobs;
                });
            }
        });

        if (!isDropped) {
            console.log("Dropped outside of valid zones.");
        }
    };

    const handleJobDrop = (index: any, job: any, packageId: string | number) => {
        setDroppedJobs((prevJobs) => {
            if (!prevJobs[index]) {
                const updatedJobs = [...prevJobs];
                updatedJobs[index] = job;
                return updatedJobs;
            }
            return prevJobs;
        });
        setJobs([])
        setPackageDeploymentExecution((prev: any) => {
            if (typeof prev === 'string') {
                return [prev, selectedTargets];
            } else {
                return [...prev, selectedTargets];
            }
        });
        setShowProgress(true)
        const jobsProgress = JSON.parse(localStorage.getItem("jobsArray"));

        const jobDataArray = jobsProgress?.map((item: {
            envName: string;
            jobData: string;
        }) => item?.jobData);

        const flattenedJobDataArray = jobDataArray?.flat();


        if (flattenedJobDataArray?.length > 0) {
            const updatedJobsPartOne = job.map((jobItem: any) => ({ ...jobItem }));
            flattenedJobDataArray.push(...updatedJobsPartOne);
            const updatedFlattenedJobDataArray = flattenedJobDataArray.map((item: any) => ({
                ...item,
                ...(item.packageId === undefined ? { packageId: packageId } : {})
            }));
            setJobsToShowProgressFor(updatedFlattenedJobDataArray);
        } else {
            const updatedFlattenedJobDataArray = job.map((item: any) => ({
                ...item,
                packageId: packageId
            }));
            setJobsToShowProgressFor(updatedFlattenedJobDataArray);
        }
    };

    const handleJobCompletion = (index: any) => {
        const updatedJobs = [...droppedJobs];
        updatedJobs[index] = null;
        setDroppedJobs(updatedJobs);
    };

    useEffect(() => {
        const handleMouseMove = (e: any) => {
            if (isDragging) {
                setPosition({ x: e.clientX, y: e.clientY });
            }
        };

        const handleMouseUp = () => {
            checkDropPosition(position.x, position.y);
            setIsDragging(false);
            setDraggedElement(null);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, position, draggedElement]);


    const handleDragStart = (e: any, jobs: any) => {
        e.dataTransfer.setData('application/json', JSON.stringify(jobs));

        const dragPreview = document.createElement('div');
        dragPreview.style.width = '150px';
        dragPreview.style.height = '50px';
        dragPreview.style.backgroundColor = '#03234a';
        dragPreview.style.color = 'white';
        dragPreview.style.borderRadius = '25px';
        dragPreview.style.padding = '10px';
        dragPreview.style.textAlign = 'center';
        dragPreview.innerText = `Package Id : ${packageId}`;

        document.body.appendChild(dragPreview);
        e.dataTransfer.setDragImage(dragPreview, 75, 25);
        e.target.addEventListener('dragend', () => {
            document.body.removeChild(dragPreview);
        }, { once: true });

    };

    const useScreenWidth = () => {
        const [width, setWidth] = useState(window.innerWidth);

        useEffect(() => {
            const onResize = () => setWidth(window.innerWidth);
            window.addEventListener("resize", onResize);
            return () => window.removeEventListener("resize", onResize);
        }, []);

        return width;
    };



    // return {
    //     flattenObjectToArray,
    //     transformDataToNested,
    //     filterListByHierarchy,
    //     extractJobIdsHierarchy,
    //     createDeploymentPkg,
    //     createDeploymentPkgInfo,
    //     saveJobData,
    //     filterSelectedItems,
    //     transformListToBackendSchema,
    //     createDynamicMappings,
    //     // setTargetEnvPackageDelivered
    // }

    return {
        // state values + setters
        jobs,
        setJobs,
        initialJobList,
        setInitialJobList,
        expandedItems,
        setExpandedItems,
        selectedHierarchy,
        setSelectedHierarchy,
        filteredItems,
        setFilteredItems,
        packageId,
        setPackageId,
        droppedJobs,
        setDroppedJobs,
        draggedElement,
        setDraggedElement,
        position,
        setPosition,
        isDragging,
        setIsDragging,
        selectedTargets,
        setSelectedTargets,
        packageDeploymentExecution,
        setPackageDeploymentExecution,
        showProgress,
        setShowProgress,
        jobToShowProgressFor,
        setJobsToShowProgressFor,
        environments,
        setEnvironments,
        showInitialSelectionModalLoader,
        setShowInitialSelectionModalLoader,
        tableNames,
        setTableNames,
        initialView,
        selectedTargetEntities,
        selectedViewTargets,
        useScreenWidth,
        // isTier1NewChecked,
        tier1NewCheckedJobs,
        hasColumnChangeReasonTaskName,
        hasColumnChangeReason,
        emptyResultWarning,
        childEntityUpdate,
        hasDependencyUpdate,
        hasTaskGroupDependencyUpdate,
        jobDependencyUpdateIds,
        taskGroupDependencyUpdateIds,
        // your existing utilities
        flattenObjectToArray,
        transformDataToNested,
        filterListByHierarchy,
        extractJobIdsHierarchy,
        createDeploymentPkg,
        targetEnvironmentId,
        createDeploymentPkgInfo,
        saveJobData,
        filterSelectedItems,
        transformListToBackendSchema,
        createDynamicMappings,
        handleJobSelect,
        handleTaskGroupSelect,
        handleJobTaskSelect,
        togglePanel,
        handleJobDrop,
        handleDragStart,
        handleJobCompletion,
        selectAllDescendants,
        extractTargetInfo,
        setSelectedViewTargets,
        setSelectedTargetEntities,
        setTier1NewCheckedJobs,
        adjustOtherGroupsOnTier1NewDeselect,
        resetAllStates,
        loading,
        setEmptyResultWarning,
        areAllJobsSelected,
        handleSelectAllJobs,
    };
}
